// src/pipeline/tiers.ts
var LatencyTier = {
  /** Unmined transactions. Fastest, may never confirm or be replaced. */
  MEMPOOL: "mempool",
  /** Mined but below the per-chain confirmation threshold. May still reorg. */
  UNCONFIRMED: "unconfirmed",
  /** At or above the per-chain confirmation threshold. Reorg risk small. */
  CONFIRMED: "confirmed",
  /** At or above the per-chain finality threshold. Will not reorg. */
  FINALIZED: "finalized"
};
var LATENCY_TIERS = [
  LatencyTier.MEMPOOL,
  LatencyTier.UNCONFIRMED,
  LatencyTier.CONFIRMED,
  LatencyTier.FINALIZED
];
var TIER_ORDER = {
  [LatencyTier.MEMPOOL]: 0,
  [LatencyTier.UNCONFIRMED]: 1,
  [LatencyTier.CONFIRMED]: 2,
  [LatencyTier.FINALIZED]: 3
};
function isAtLeast(actual, required) {
  return TIER_ORDER[actual] >= TIER_ORDER[required];
}
var TIER_LABEL = {
  [LatencyTier.MEMPOOL]: "Mempool",
  [LatencyTier.UNCONFIRMED]: "Unconfirmed",
  [LatencyTier.CONFIRMED]: "Confirmed",
  [LatencyTier.FINALIZED]: "Finalized"
};
var Completeness = {
  PRELIMINARY: "preliminary",
  COMPLETE: "complete"
};

// src/pipeline/policies.ts
var FailurePolicy = {
  /** Abort the whole pipeline run. */
  FAIL_PIPELINE: "fail_pipeline",
  /** Mark this step failed; skip steps that `needs` it; continue others. */
  FAIL_STEP: "fail_step",
  /** Soft failure; emit a degraded result and continue. */
  DEGRADE: "degrade"
};
var FAILURE_POLICIES = [
  FailurePolicy.FAIL_PIPELINE,
  FailurePolicy.FAIL_STEP,
  FailurePolicy.DEGRADE
];
var FAILURE_POLICY_LABEL = {
  [FailurePolicy.FAIL_PIPELINE]: "Fail pipeline",
  [FailurePolicy.FAIL_STEP]: "Fail step",
  [FailurePolicy.DEGRADE]: "Degrade"
};
var FAILURE_POLICY_DESCRIPTION = {
  [FailurePolicy.FAIL_PIPELINE]: "Abort the whole run if this step raises.",
  [FailurePolicy.FAIL_STEP]: "Fail this step and skip its dependents; continue the rest.",
  [FailurePolicy.DEGRADE]: "Treat as a soft failure; emit a degraded result and continue."
};
var StepStatus = {
  OK: "ok",
  DEGRADED: "degraded",
  FAILED: "failed",
  // First-class skip (feature flag off, tier unsupported, upstream skipped,
  // dependency not wired, in-step no-op). Mirrors
  // `authe_tx_pipeline.core.step.StepStatus.SKIPPED`; never rolls a run up to
  // `failed`. The `StepDecision` on the outcome carries *why*.
  SKIPPED: "skipped"
};
var STEP_STATUS_LABEL = {
  [StepStatus.OK]: "OK",
  [StepStatus.DEGRADED]: "Degraded",
  [StepStatus.FAILED]: "Failed",
  [StepStatus.SKIPPED]: "Skipped"
};
var DEFAULT_RETRY_POLICY = {
  maxAttempts: 1,
  initialBackoffSeconds: 0.5,
  backoffMultiplier: 2,
  maxBackoffSeconds: 30
};
function retriesEnabled(policy) {
  return policy.maxAttempts > 1;
}

// src/pipeline/step.ts
var StepCategory = {
  SOURCE: "source",
  NORMALIZE: "normalize",
  DECODE: "decode",
  PROTOCOL: "protocol",
  ENRICH: "enrich",
  GRAPH: "graph",
  CORRELATE: "correlate",
  PERSIST: "persist",
  INDEX: "index",
  TRIGGER: "trigger",
  REORG: "reorg",
  ML: "ml",
  QUOTA: "quota",
  CUSTOM: "custom"
};
var STEP_CATEGORY_LABEL = {
  [StepCategory.SOURCE]: "Sources",
  [StepCategory.NORMALIZE]: "Normalize",
  [StepCategory.DECODE]: "Decode & Parse",
  [StepCategory.PROTOCOL]: "Protocol",
  [StepCategory.ENRICH]: "Enrichment",
  [StepCategory.GRAPH]: "Graph & Entity",
  [StepCategory.CORRELATE]: "Correlation / SAGA",
  [StepCategory.PERSIST]: "Persistence",
  [StepCategory.INDEX]: "Indexing",
  [StepCategory.TRIGGER]: "Triggers",
  [StepCategory.REORG]: "Reorg & Finality",
  [StepCategory.ML]: "ML",
  [StepCategory.QUOTA]: "Quota & Billing",
  [StepCategory.CUSTOM]: "Custom"
};
function stepRefId(meta) {
  return `${meta.name}@${meta.version}`;
}
function isEnrichmentStep(meta) {
  return meta.category === "enrich" || meta.name.startsWith("enrich_") || meta.name.endsWith("_enrich");
}
function defaultStepMetadata(partial) {
  return {
    version: "0.1.0",
    owner: { team: "unassigned" },
    supportedTiers: [],
    needs: [],
    failurePolicy: FailurePolicy.FAIL_STEP,
    retryPolicy: { ...DEFAULT_RETRY_POLICY },
    requiredFlags: [],
    options: [],
    category: StepCategory.CUSTOM,
    ...partial
  };
}

// src/pipeline/outcome.ts
var StepDecisionReason = {
  RAN: "ran",
  FEATURE_FLAG_OFF: "feature_flag_off",
  TIER_UNSUPPORTED: "tier_unsupported",
  UPSTREAM_FAILED: "upstream_failed",
  UPSTREAM_SKIPPED: "upstream_skipped",
  IDEMPOTENCY_HIT: "idempotency_hit",
  DEPENDENCY_NOT_WIRED: "dependency_not_wired",
  NO_INPUT: "no_input",
  POLICY_DISABLED: "policy_disabled",
  NOTHING_TO_DO: "nothing_to_do",
  FAILED: "failed"
};
var SKIP_REASONS = /* @__PURE__ */ new Set([
  StepDecisionReason.FEATURE_FLAG_OFF,
  StepDecisionReason.TIER_UNSUPPORTED,
  StepDecisionReason.UPSTREAM_FAILED,
  StepDecisionReason.UPSTREAM_SKIPPED,
  StepDecisionReason.IDEMPOTENCY_HIT,
  StepDecisionReason.DEPENDENCY_NOT_WIRED,
  StepDecisionReason.NO_INPUT,
  StepDecisionReason.POLICY_DISABLED
]);
function isSkipReason(reason) {
  return reason !== void 0 && SKIP_REASONS.has(reason);
}
function isSkipped(outcome) {
  return outcome.status === "skipped" || isSkipReason(outcome.decision?.reason) || outcome.attempts === 0 || (outcome.error?.startsWith("skipped:") ?? false);
}
function outcomeReason(outcome) {
  const decision = outcome.decision;
  if (decision && decision.reason !== StepDecisionReason.RAN) {
    return decision.summary || decision.reason;
  }
  return outcome.error ?? void 0;
}
function summarizeOutcomes(outcomes) {
  let ok = 0;
  let degraded = 0;
  let failed = 0;
  let skipped = 0;
  for (const o of outcomes) {
    if (isSkipped(o)) {
      skipped += 1;
      continue;
    }
    if (o.status === "failed") failed += 1;
    else if (o.status === "degraded") degraded += 1;
    else ok += 1;
  }
  return { ok, degraded, failed, skipped };
}

// src/chains/family.ts
var ChainFamily = {
  EVM: "evm",
  UTXO: "utxo",
  SOLANA: "solana",
  COSMOS: "cosmos",
  OTHER: "other"
};

// src/chains/seed-data.ts
var CHAIN_MAPPING = {
  // --- EVM mainnets ---
  "0x1": "eth",
  "0x3d": "etc",
  "0x89": "polygon",
  "0x38": "bsc",
  "0xcc": "opbnb",
  "0xa86a": "avalanche",
  "0xfa": "fantom",
  "0x19": "cronos",
  "0x2a15c308d": "palm",
  "0xa4b1": "arbitrum",
  "0x64": "gnosis",
  "0x15b38": "chiliz",
  "0x2105": "base",
  "0xa": "optimism",
  "0x2019": "klaytn",
  "0xa4ec": "celo",
  "0x8ae": "kava",
  "0x440": "metis",
  "0x63564c40": "harmony",
  "0x171": "pulsechain",
  "0x1388": "mantle",
  // Nesa. A Cosmos-SDK chain with an EVM module, so its Cosmos chain id
  // (`nesa_41443-1`) and its EVM chain id are the same 41443. Ported from
  // `_seed_data.py`, which has carried it since the Hyperlane domain fallback
  // was retired — without this row every spelling of Nesa falls through the
  // registry unresolved and renders as a raw `eip155:41443`.
  "0xa1e3": "nesa",
  "0x505": "moonriver",
  "0x504": "moonbeam",
  "0x144": "zkSync_era",
  "0x44d": "polygon_zkevm",
  "0xe708": "linea",
  "0x82750": "scroll",
  "0x13e31": "blast",
  "0x4e454152": "aurora",
  "0x7a": "fuse",
  "0x2eb": "flow",
  "0x7e4": "ronin",
  "0x46f": "lisk",
  "0x1b58": "zetachain",
  "0x531": "sei",
  "0x8f": "monad",
  "0x2611": "plasma",
  // --- EVM testnets ---
  "0x5": "goerli",
  "0xaa36a7": "sepolia",
  "0x4268": "holesky",
  "0x13881": "mumbai",
  "0x13882": "polygon_amoy",
  "0x61": "bsc_testnet",
  "0x27d8": "gnosis_chiado",
  "0x15b32": "chiliz_spicy",
  "0x14a33": "base_goerli",
  "0x14a34": "base_sepolia",
  "0x1a4": "optimism_goerli",
  "0xaa37dc": "optimism_sepolia",
  "0x66eee": "arbitrum_sepolia",
  "0x3e9": "klaytn_baobab",
  "0xaef3": "celo_alfajores",
  "0x8ad": "kava_testnet",
  "0x24c": "metis_goerli",
  "0x6357d2e0": "harmony_testnet",
  "0x4e454153": "aurora_testnet",
  "0x507": "moonbase",
  "0x118": "zkSync_testnet",
  "0x145": "zkSync_era_testnet",
  "0x12c": "zksync_sepolia",
  "0x7b": "fuse_testnet",
  "0xe704": "linea_testnet",
  "0xe705": "linea_sepolia",
  "0x8274f": "scroll_testnet",
  "0x138b": "mantle_sepolia",
  "0xa0c71fd": "blast_sepolia",
  "0x98a": "polygon_zkevm_cardona",
  "0xfa2": "fantom_testnet",
  "0x106a": "lisk_sepolia",
  "0x221": "flow_testnet",
  "0x7e5": "ronin_saigon",
  "0x1b59": "zetachain_testnet",
  "0x530": "sei_testnet"
};
var TESTNET_CHAINS = [
  "goerli",
  "sepolia",
  "mumbai",
  "bsc_testnet",
  "gnosis_chiado",
  "chiliz_spicy",
  "base_goerli",
  "optimism_goerli",
  "klaytn_baobab",
  "celo_alfajores",
  "kava_testnet",
  "metis_goerli",
  "harmony_testnet",
  "zkSync_testnet",
  "aurora_testnet",
  "moonbase",
  "zkSync_era_testnet",
  "fuse_testnet",
  "linea_testnet",
  "scroll_testnet",
  "holesky",
  "polygon_amoy",
  "base_sepolia",
  "optimism_sepolia",
  "arbitrum_sepolia",
  "linea_sepolia",
  "blast_sepolia",
  "mantle_sepolia",
  "zksync_sepolia",
  "polygon_zkevm_cardona",
  "fantom_testnet",
  "lisk_sepolia",
  "flow_testnet",
  "ronin_saigon",
  "zetachain_testnet",
  "sei_testnet"
];
var LAYER1_CHAINS = [
  "eth",
  "etc",
  "bsc",
  "avalanche",
  "fantom",
  "klaytn",
  "celo",
  "kava",
  "harmony",
  "moonriver",
  "moonbeam",
  "fuse",
  "pulsechain",
  "flow",
  "ronin",
  "sei",
  "monad",
  "zetachain"
];
var LAYER2_CHAINS = [
  "polygon",
  "arbitrum",
  "optimism",
  "metis",
  "base",
  "zkSync_era",
  "linea",
  "scroll",
  "mantle",
  "opbnb",
  "polygon_zkevm",
  "blast",
  "lisk"
];
var SIDECHAINS = ["palm", "gnosis", "cronos", "aurora"];
var CHAIN_CONFIG = {
  "0x1": { token: "ETH", gasUnit: "Gwei", name: "Ethereum" },
  "0x3d": { token: "ETC", gasUnit: "Gwei", name: "Ethereum Classic" },
  "0x89": { token: "MATIC", gasUnit: "Gwei", name: "Polygon" },
  "0x38": { token: "BNB", gasUnit: "Gwei", name: "BNB Chain" },
  "0xa4b1": { token: "ARB", gasUnit: "Gwei", name: "Arbitrum" },
  "0x2105": { token: "ETH", gasUnit: "Gwei", name: "Base" },
  "0xa": { token: "ETH", gasUnit: "Gwei", name: "Optimism" },
  "0xe708": { token: "ETH", gasUnit: "Gwei", name: "Linea" },
  "0xa86a": { token: "AVAX", gasUnit: "nAVAX", name: "Avalanche" },
  "0xfa": { token: "FTM", gasUnit: "Gwei", name: "Fantom" },
  "0x19": { token: "CRO", gasUnit: "Gwei", name: "Cronos" },
  "0x64": { token: "xDAI", gasUnit: "Gwei", name: "Gnosis" },
  "0x15b38": { token: "CHZ", gasUnit: "Gwei", name: "Chiliz" },
  "0x171": { token: "PLS", gasUnit: "Gwei", name: "PulseChain" },
  "0x504": { token: "GLMR", gasUnit: "Gwei", name: "Moonbeam" },
  "0x505": { token: "MOVR", gasUnit: "Gwei", name: "Moonriver" },
  "0x13e31": { token: "ETH", gasUnit: "Gwei", name: "Blast" },
  "0x144": { token: "ETH", gasUnit: "Gwei", name: "zkSync Era" },
  "0x1388": { token: "MNT", gasUnit: "Gwei", name: "Mantle" },
  "0xa1e3": { token: "NES", gasUnit: "Gwei", name: "Nesa" },
  "0xcc": { token: "BNB", gasUnit: "Gwei", name: "opBNB" },
  // Gas on Polygon zkEVM is ETH, not MATIC.
  "0x44d": { token: "ETH", gasUnit: "Gwei", name: "Polygon zkEVM" },
  "0x1b58": { token: "ZETA", gasUnit: "Gwei", name: "ZetaChain" },
  "0x82750": { token: "ETH", gasUnit: "Gwei", name: "Scroll" },
  "0x4e454152": { token: "ETH", gasUnit: "Gwei", name: "Aurora" },
  "0x531": { token: "SEI", gasUnit: "Gwei", name: "Sei" },
  "0x8f": { token: "MON", gasUnit: "Gwei", name: "Monad" },
  "0x3e9": { token: "KLAY", gasUnit: "Gwei", name: "Klaytn Baobab" },
  "0x4e454153": { token: "ETH", gasUnit: "Gwei", name: "Aurora Testnet" },
  "0x8274f": { token: "ETH", gasUnit: "Gwei", name: "Scroll Sepolia" },
  "0x530": { token: "SEI", gasUnit: "Gwei", name: "Sei Testnet" },
  "0x2eb": { token: "FLOW", gasUnit: "FLOW units", name: "Flow" },
  "0x7e4": { token: "RON", gasUnit: "Gwei", name: "Ronin" },
  "0x46f": { token: "LSK", gasUnit: "LSK units", name: "Lisk" },
  "0xaa36a7": { token: "ETH", gasUnit: "Gwei", name: "Sepolia" },
  "0x4268": { token: "ETH", gasUnit: "Gwei", name: "Holesky" },
  "0x13882": { token: "MATIC", gasUnit: "Gwei", name: "Polygon Amoy" },
  "0x61": { token: "tBNB", gasUnit: "Gwei", name: "BSC Testnet" },
  "0x66eee": { token: "ARB", gasUnit: "Gwei", name: "Arbitrum Sepolia" },
  "0x14a34": { token: "ETH", gasUnit: "Gwei", name: "Base Sepolia" },
  "0xaa37dc": { token: "ETH", gasUnit: "Gwei", name: "Optimism Sepolia" },
  "0xe705": { token: "ETH", gasUnit: "Gwei", name: "Linea Sepolia" },
  "0xfa2": { token: "FTM", gasUnit: "Gwei", name: "Fantom Testnet" },
  "0x27d8": { token: "xDAI", gasUnit: "Gwei", name: "Gnosis Chiado" },
  "0x15b32": { token: "CHZ", gasUnit: "Gwei", name: "Chiliz Testnet" },
  "0x507": { token: "DEV", gasUnit: "Gwei", name: "Moonbase" },
  "0xa0c71fd": { token: "ETH", gasUnit: "Gwei", name: "Blast Sepolia" },
  "0x12c": { token: "ETH", gasUnit: "Gwei", name: "zkSync Sepolia" },
  "0x138b": { token: "MNT", gasUnit: "Gwei", name: "Mantle Sepolia" },
  "0x98a": { token: "MATIC", gasUnit: "Gwei", name: "Polygon zkEVM Cardona" },
  "0x1b59": { token: "ZETA", gasUnit: "Gwei", name: "Zetachain Testnet" },
  "0x221": { token: "FLOW", gasUnit: "FLOW units", name: "Flow Testnet" },
  "0x7e5": { token: "RON", gasUnit: "Gwei", name: "Ronin Saigon Testnet" },
  "0x106a": { token: "LSK", gasUnit: "LSK units", name: "Lisk Sepolia" },
  "0x0": { token: "BTC", gasUnit: "Sats/byte", name: "Bitcoin" },
  "0x1399c": { token: "SOL", gasUnit: "Lamports", name: "Solana" },
  "0x162": { token: "DOT", gasUnit: "Plancks", name: "Polkadot" },
  "0x2611": { token: "XPL", gasUnit: "Gwei", name: "Plasma Mainnet" }
};
var EXPLORER_URLS = {
  eth: "https://etherscan.io",
  etc: "https://blockscout.com/etc/mainnet",
  polygon: "https://polygonscan.com",
  bsc: "https://bscscan.com",
  avalanche: "https://snowtrace.io",
  fantom: "https://ftmscan.com",
  arbitrum: "https://arbiscan.io",
  optimism: "https://optimistic.etherscan.io",
  zksync_era: "https://explorer.zksync.io",
  base: "https://basescan.org",
  linea: "https://lineascan.build",
  scroll: "https://scrollscan.com",
  mantle: "https://explorer.mantle.xyz",
  // The EVM-side Blockscout. Nesa also runs a Cosmos explorer at
  // `explorer.nesa.ai`, which cannot resolve an `0x` address or tx hash.
  nesa: "https://explorer-evm.nesa.ai",
  opbnb: "https://opbnbscan.com",
  polygon_zkevm: "https://zkevm.polygonscan.com",
  blast: "https://blastscan.io",
  pulsechain: "https://scan.pulsechain.com",
  aurora: "https://explorer.aurora.dev",
  flow: "https://evm.flowscan.io",
  ronin: "https://app.roninchain.com",
  lisk: "https://blockscout.lisk.com",
  zetachain: "https://explorer.zetachain.com",
  sei: "https://seitrace.com",
  monad: "https://monadexplorer.com",
  goerli: "https://goerli.etherscan.io",
  sepolia: "https://sepolia.etherscan.io",
  mumbai: "https://mumbai.polygonscan.com",
  plasma: "https://plasmascan.to"
};
var TESTNET_TO_MAINNET = {
  goerli: "eth",
  sepolia: "eth",
  mumbai: "polygon",
  bsc_testnet: "bsc",
  gnosis_chiado: "gnosis",
  chiliz_spicy: "chiliz",
  base_goerli: "base",
  optimism_goerli: "optimism",
  klaytn_baobab: "klaytn",
  celo_alfajores: "celo",
  kava_testnet: "kava",
  metis_goerli: "metis",
  harmony_testnet: "harmony",
  zkSync_testnet: "zkSync_era",
  aurora_testnet: "aurora",
  moonbase: "moonbeam",
  zkSync_era_testnet: "zkSync_era",
  fuse_testnet: "fuse",
  linea_testnet: "linea",
  scroll_testnet: "scroll",
  holesky: "eth",
  polygon_amoy: "polygon",
  base_sepolia: "base",
  optimism_sepolia: "optimism",
  arbitrum_sepolia: "arbitrum",
  linea_sepolia: "linea",
  blast_sepolia: "blast",
  mantle_sepolia: "mantle",
  zksync_sepolia: "zkSync_era",
  polygon_zkevm_cardona: "polygon_zkevm",
  fantom_testnet: "fantom",
  lisk_sepolia: "lisk",
  flow_testnet: "flow",
  ronin_saigon: "ronin",
  zetachain_testnet: "zetachain",
  sei_testnet: "sei"
};

// src/chains/registry.ts
var ChainRegistry = class {
  bySlug = /* @__PURE__ */ new Map();
  byHex = /* @__PURE__ */ new Map();
  byCaip2 = /* @__PURE__ */ new Map();
  register(chain, { replace = false } = {}) {
    const existingSlug = this.bySlug.get(chain.slug);
    if (existingSlug && existingSlug !== chain && !replace) {
      throw new Error(
        `Chain slug "${chain.slug}" already registered; pass replace=true to overwrite.`
      );
    }
    if (chain.chainIdHex) {
      const existingHex = this.byHex.get(chain.chainIdHex);
      if (existingHex && existingHex.slug !== chain.slug && !replace) {
        throw new Error(
          `Chain hex ${chain.chainIdHex} already mapped to slug ${existingHex.slug}; pass replace=true to overwrite.`
        );
      }
    }
    this.bySlug.set(chain.slug, chain);
    if (chain.chainIdHex) this.byHex.set(chain.chainIdHex, chain);
    if (chain.caip2) this.byCaip2.set(chain.caip2, chain);
  }
  unregister(slug) {
    const chain = this.bySlug.get(slug);
    if (!chain) return null;
    this.bySlug.delete(slug);
    if (chain.chainIdHex) this.byHex.delete(chain.chainIdHex);
    if (chain.caip2) this.byCaip2.delete(chain.caip2);
    return chain;
  }
  clear() {
    this.bySlug.clear();
    this.byHex.clear();
    this.byCaip2.clear();
  }
  get(slug) {
    return this.bySlug.get(slug) ?? null;
  }
  /** Lookup by EVM hex chain id (auto `0x`-prefixes + lowercases). */
  getByHex(chainIdHex) {
    let normalized = chainIdHex.toLowerCase();
    if (!normalized.startsWith("0x")) normalized = "0x" + normalized.replace(/^0x/, "");
    return this.byHex.get(normalized) ?? null;
  }
  /** Lookup by CAIP-2 chain id (e.g. "eip155:1"). */
  getByCaip2(caip2) {
    return this.byCaip2.get(caip2.trim().toLowerCase()) ?? null;
  }
  /** Resolve by CAIP-2, hex, OR slug. */
  resolve(value) {
    if (!value) return null;
    if (value.includes(":")) {
      const hit = this.getByCaip2(value);
      if (hit) return hit;
    }
    if (value.startsWith("0x")) {
      const hit = this.getByHex(value);
      if (hit) return hit;
    }
    if (/^\d+$/.test(value)) {
      const hit = this.getByHex("0x" + BigInt(value).toString(16));
      if (hit) return hit;
    }
    return this.get(value.toLowerCase());
  }
  byFamily(family) {
    return [...this.bySlug.values()].filter((c) => c.family === family).sort((a, b) => a.slug.localeCompare(b.slug));
  }
  all() {
    return [...this.bySlug.values()].sort((a, b) => a.slug.localeCompare(b.slug));
  }
};
var registry = new ChainRegistry();
var DEFAULT_CHAIN_ID = "eip155:1";
function toCanonicalChainId(value) {
  if (!value) return null;
  const chain = registry.resolve(value);
  if (!chain) return value.trim().toLowerCase() || null;
  return chain.caip2 ?? chain.chainIdHex ?? chain.slug;
}
function resolveChain(value) {
  return registry.resolve(value);
}
function getChainMetadata(chainId) {
  return registry.resolve(chainId);
}
function chainDisplayName(value) {
  if (!value) return "";
  return getChainMetadata(value)?.name ?? value;
}
var REORG_DEPTHS_BY_SLUG = {
  eth: 12,
  polygon: 256,
  matic: 256,
  bsc: 15,
  arbitrum: 12,
  optimism: 12,
  base: 12,
  avalanche: 12,
  fantom: 12,
  cronos: 12,
  gnosis: 12,
  linea: 12,
  scroll: 12,
  mantle: 12,
  zksync: 12,
  zksync_era: 12,
  metis: 12,
  celo: 12,
  klaytn: 12,
  moonbeam: 12,
  moonriver: 12,
  harmony: 12,
  aurora: 12,
  fuse: 12,
  palm: 12,
  chiliz: 12,
  kava: 12,
  blast: 12,
  bitcoin: 6,
  solana: 32,
  // Monero's ten-block spend lock is a network rule; Zcash needs many more
  // blocks than Bitcoin for equal work at a 75-second block.
  monero: 10,
  zcash: 24
};
var BLOCK_TIME_BY_SLUG = {
  eth: 12,
  etc: 13,
  polygon: 2,
  matic: 2,
  bsc: 3,
  arbitrum: 0.25,
  optimism: 2,
  base: 2,
  avalanche: 2,
  fantom: 1,
  cronos: 6,
  gnosis: 5,
  linea: 3,
  scroll: 3,
  mantle: 2,
  zksync: 1,
  zksync_era: 1,
  metis: 4,
  celo: 5,
  klaytn: 1,
  moonbeam: 12,
  moonriver: 12,
  harmony: 2,
  aurora: 1,
  fuse: 5,
  blast: 2,
  bitcoin: 600,
  solana: 0.4,
  polkadot: 6,
  monero: 120,
  zcash: 75
};
var EXPLORER_SUPPLEMENT = {
  bitcoin: "https://blockstream.info",
  solana: "https://solscan.io",
  polkadot: "https://polkadot.subscan.io",
  monero: "https://xmrchain.net",
  // Blockchair's base carries the coin as a path segment, which is exactly
  // what the link builder appends to — `/zcash` + `/transaction/<hash>`.
  zcash: "https://blockchair.com/zcash",
  cronos: "https://cronoscan.com",
  gnosis: "https://gnosisscan.io",
  celo: "https://celoscan.io",
  moonbeam: "https://moonscan.io",
  zkSync_era: "https://explorer.zksync.io",
  matic: "https://polygonscan.com"
};
function explorerFor(slug, rawSlug, mainnetSlug) {
  return EXPLORER_URLS[slug] ?? EXPLORER_URLS[rawSlug] ?? EXPLORER_SUPPLEMENT[slug] ?? EXPLORER_SUPPLEMENT[rawSlug] ?? (mainnetSlug ? EXPLORER_URLS[mainnetSlug] ?? EXPLORER_SUPPLEMENT[mainnetSlug] ?? null : null);
}
var BRAND_COLOR_BY_SLUG = {
  eth: "#627EEA",
  etc: "#328332",
  polygon: "#8247E5",
  matic: "#8247E5",
  bsc: "#F0B90B",
  arbitrum: "#28A0F0",
  optimism: "#FF0420",
  base: "#0052FF",
  avalanche: "#E84142",
  fantom: "#1969FF",
  cronos: "#002D74",
  gnosis: "#0B6E4F",
  linea: "#61DFFF",
  scroll: "#EBC28E",
  mantle: "#000000",
  zksync: "#8C8DFC",
  zksync_era: "#8C8DFC",
  metis: "#00DACC",
  celo: "#FCFF52",
  klaytn: "#FF1B1B",
  moonbeam: "#53CBC8",
  moonriver: "#C0C0C0",
  harmony: "#00AEE9",
  aurora: "#70D44B",
  fuse: "#B4F9BA",
  blast: "#FCFC03",
  kava: "#FF433E",
  bitcoin: "#F7931A",
  solana: "#14F195",
  polkadot: "#E6007A",
  monero: "#FF6600",
  zcash: "#F4B728"
};
function brandColorFor(slug, mainnetSlug) {
  return BRAND_COLOR_BY_SLUG[slug] ?? (mainnetSlug ? BRAND_COLOR_BY_SLUG[mainnetSlug] ?? null : null);
}
var LOGO_URL_BY_SLUG = {};
var CAIP2_BY_SLUG = {
  bitcoin: "bip122:000000000019d6689c085ae165831e93",
  solana: "solana:5eykt4usfv8p8njdtrepy1vzqkqzkvdp",
  polkadot: "polkadot:91b171bb158e2d3848fa23a9f1c25182",
  // Zcash is a Bitcoin fork, so `bip122` is its registered namespace and the
  // reference is its genesis hash truncated to 32 chars — Bitcoin's own rule.
  zcash: "bip122:00040fe8ec8471911baa1db1266ea15d",
  // Monero has no CASA-registered namespace; `monero` is a platform-local
  // choice. See the note on the Python side, which this table must match
  // byte for byte — `test_chains_fe_registry_parity.py` fails if it drifts.
  monero: "monero:418015bb9ae982a1975da7d79277c270"
};
function deriveCaip2(slug, family, chainIdHex) {
  if (CAIP2_BY_SLUG[slug]) return CAIP2_BY_SLUG[slug];
  if (family === ChainFamily.EVM && chainIdHex) {
    return `eip155:${BigInt(chainIdHex).toString(10)}`;
  }
  return null;
}
var NON_EVM_OVERRIDES = {
  bitcoin: { family: ChainFamily.UTXO, decimals: 8, chainIdHex: null, name: "Bitcoin", nativeToken: "BTC" },
  solana: { family: ChainFamily.SOLANA, decimals: 9, chainIdHex: null, name: "Solana", nativeToken: "SOL" },
  polkadot: { family: ChainFamily.OTHER, decimals: 10, chainIdHex: null, name: "Polkadot", nativeToken: "DOT" },
  monero: { family: ChainFamily.UTXO, decimals: 12, chainIdHex: null, name: "Monero", nativeToken: "XMR" },
  zcash: { family: ChainFamily.UTXO, decimals: 8, chainIdHex: null, name: "Zcash", nativeToken: "ZEC" }
};
function layerFor(slug) {
  if (LAYER1_CHAINS.includes(slug)) return "l1";
  if (LAYER2_CHAINS.includes(slug)) return "l2";
  if (SIDECHAINS.includes(slug)) return "sidechain";
  return "unknown";
}
function seedFromCanonical() {
  for (const [hexId, rawSlug] of Object.entries(CHAIN_MAPPING)) {
    const slug = rawSlug.toLowerCase();
    const config = CHAIN_CONFIG[hexId];
    const token = config?.token ?? "ETH";
    const override = NON_EVM_OVERRIDES[slug];
    const family = override?.family ?? ChainFamily.EVM;
    const decimals = override?.decimals ?? 18;
    const isTestnet = TESTNET_CHAINS.includes(rawSlug);
    let mainnetSlug = null;
    if (isTestnet) {
      const m = TESTNET_TO_MAINNET[rawSlug];
      if (m) mainnetSlug = m.toLowerCase();
    }
    const reorgDepth = REORG_DEPTHS_BY_SLUG[slug] ?? 12;
    const chainIdHex = hexId.toLowerCase();
    const metadata = { layer: layerFor(slug) };
    if (config?.gasUnit) metadata.gasUnit = config.gasUnit;
    if (slug === "etc") {
      metadata.fork_of = "eth";
      metadata.note = "ETC shares pre-fork tx hashes with ETH. Audit sec. 2 calls out this collision risk.";
    }
    try {
      registry.register(
        {
          slug,
          chainIdHex,
          family,
          name: config?.name ?? rawSlug,
          nativeToken: token,
          decimals,
          isTestnet,
          mainnetSlug,
          reorgDepth,
          explorerUrl: explorerFor(slug, rawSlug, mainnetSlug),
          blockTimeSec: BLOCK_TIME_BY_SLUG[slug] ?? (mainnetSlug ? BLOCK_TIME_BY_SLUG[mainnetSlug] ?? null : null),
          caip2: deriveCaip2(slug, family, chainIdHex),
          brandColor: brandColorFor(slug, mainnetSlug),
          logoUrl: LOGO_URL_BY_SLUG[slug] ?? (mainnetSlug ? LOGO_URL_BY_SLUG[mainnetSlug] ?? null : null),
          metadata
        },
        { replace: true }
      );
    } catch {
      continue;
    }
  }
  for (const [slug, ovr] of Object.entries(NON_EVM_OVERRIDES)) {
    if (registry.get(slug)) continue;
    try {
      registry.register(
        {
          slug,
          chainIdHex: ovr.chainIdHex,
          family: ovr.family,
          name: ovr.name,
          nativeToken: ovr.nativeToken,
          decimals: ovr.decimals,
          isTestnet: false,
          mainnetSlug: null,
          reorgDepth: REORG_DEPTHS_BY_SLUG[slug] ?? 12,
          explorerUrl: explorerFor(slug, slug, null),
          blockTimeSec: BLOCK_TIME_BY_SLUG[slug] ?? null,
          caip2: deriveCaip2(slug, ovr.family, ovr.chainIdHex),
          brandColor: brandColorFor(slug, null),
          logoUrl: LOGO_URL_BY_SLUG[slug] ?? null,
          metadata: {}
        },
        { replace: true }
      );
    } catch {
      continue;
    }
  }
}
seedFromCanonical();

// src/chains/address.ts
var RE_EVM = /^0x[0-9a-fA-F]{40}$/;
var RE_BTC_BASE58 = /^(1|3)[1-9A-HJ-NP-Za-km-z]{25,39}$/;
var RE_BTC_BECH32 = /^(bc1|tb1)[0-9ac-hj-np-z]{11,71}$/;
var RE_SOLANA = /^[1-9A-HJ-NP-Za-km-z]{32,44}$/;
var RE_COSMOS_BECH32 = /^[a-z][a-z0-9]{1,10}1[0-9ac-hj-np-z]{38,58}$/;
function addressFamily(address, chainId) {
  if (chainId) {
    const fam = getChainMetadata(chainId)?.family;
    if (fam) return fam;
  }
  if (RE_EVM.test(address)) return ChainFamily.EVM;
  if (RE_BTC_BASE58.test(address) || RE_BTC_BECH32.test(address)) return ChainFamily.UTXO;
  if (RE_SOLANA.test(address)) return ChainFamily.SOLANA;
  if (RE_COSMOS_BECH32.test(address)) return ChainFamily.COSMOS;
  return null;
}
function normalizeAddress(address, chainId) {
  const addr = address.trim();
  const fam = addressFamily(addr, chainId);
  if (fam === ChainFamily.EVM) {
    const hex = addr.toLowerCase();
    return hex.startsWith("0x") ? hex : `0x${hex}`;
  }
  return addr;
}
function isValidAddress(address, chainId) {
  const addr = address.trim();
  if (!addr) return false;
  const fam = chainId ? getChainMetadata(chainId)?.family ?? null : addressFamily(addr);
  switch (fam) {
    case ChainFamily.EVM:
      return RE_EVM.test(addr);
    case ChainFamily.UTXO:
      return RE_BTC_BASE58.test(addr) || RE_BTC_BECH32.test(addr);
    case ChainFamily.SOLANA:
      return RE_SOLANA.test(addr);
    case ChainFamily.COSMOS:
      return RE_COSMOS_BECH32.test(addr);
    default:
      return RE_EVM.test(addr) || RE_BTC_BASE58.test(addr) || RE_BTC_BECH32.test(addr) || RE_SOLANA.test(addr) || RE_COSMOS_BECH32.test(addr);
  }
}
function stableHash(input) {
  let h = 2166136261;
  for (let i = 0; i < input.length; i += 1) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return (h >>> 0).toString(16).padStart(8, "0");
}

// src/pipeline/config.ts
var ConfigLayerKind = {
  GLOBAL: "global",
  CHAIN: "chain",
  PROTOCOL: "protocol",
  CUSTOMER: "customer",
  ORG: "org",
  TENANT: "tenant",
  USER: "user",
  TX_TYPE: "tx_type",
  ENTITY: "entity"
};
var CONFIG_LAYER_ORDER = [
  ConfigLayerKind.GLOBAL,
  ConfigLayerKind.CHAIN,
  ConfigLayerKind.PROTOCOL,
  ConfigLayerKind.CUSTOMER,
  ConfigLayerKind.ORG,
  ConfigLayerKind.TENANT,
  ConfigLayerKind.USER,
  ConfigLayerKind.TX_TYPE,
  ConfigLayerKind.ENTITY
];
var CONFIG_LAYER_LABEL = {
  [ConfigLayerKind.GLOBAL]: "Global",
  [ConfigLayerKind.CHAIN]: "Chain",
  [ConfigLayerKind.PROTOCOL]: "Protocol",
  [ConfigLayerKind.CUSTOMER]: "Customer",
  [ConfigLayerKind.ORG]: "Org",
  [ConfigLayerKind.TENANT]: "Tenant",
  [ConfigLayerKind.USER]: "User",
  [ConfigLayerKind.TX_TYPE]: "Tx type",
  [ConfigLayerKind.ENTITY]: "Entity"
};
function emptyLayer() {
  return { inherit: true, values: {}, extends: [] };
}
function emptyHierarchicalConfig() {
  return {
    global: emptyLayer(),
    chain: {},
    protocol: {},
    customer: {},
    org: {},
    tenant: {},
    user: {},
    tx_type: {},
    entity: {}
  };
}
function isPlainObject(v) {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}
function deepMergeValues(base, patch) {
  const out = { ...base };
  for (const [key, patchVal] of Object.entries(patch)) {
    if (key in out) {
      const baseVal = out[key];
      if (isPlainObject(baseVal) && isPlainObject(patchVal)) {
        out[key] = deepMergeValues(baseVal, patchVal);
        continue;
      }
      if (Array.isArray(baseVal) && Array.isArray(patchVal)) {
        out[key] = [...baseVal, ...patchVal];
        continue;
      }
    }
    out[key] = patchVal;
  }
  return out;
}
var KEYED_LAYER_KINDS = CONFIG_LAYER_ORDER.filter(
  (kind) => kind !== ConfigLayerKind.GLOBAL
);
var KEYED_LAYER_KIND_SET = new Set(KEYED_LAYER_KINDS);
var SCOPE_KEY_FOR = {
  [ConfigLayerKind.CHAIN]: "chainId",
  [ConfigLayerKind.PROTOCOL]: "protocolId",
  [ConfigLayerKind.CUSTOMER]: "customerId",
  [ConfigLayerKind.ORG]: "orgId",
  [ConfigLayerKind.TENANT]: "tenantId",
  [ConfigLayerKind.USER]: "userId",
  [ConfigLayerKind.TX_TYPE]: "txType",
  [ConfigLayerKind.ENTITY]: "entityId"
};
function lookupLayer(config, label) {
  if (label === "global") return config.global;
  const sep = label.indexOf(":");
  if (sep < 0) return void 0;
  const kind = label.slice(0, sep);
  if (!KEYED_LAYER_KIND_SET.has(kind)) return void 0;
  return config[kind]?.[label.slice(sep + 1)];
}
function stableStringify(value) {
  if (value === null || typeof value !== "object") return JSON.stringify(value) ?? "null";
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(",")}]`;
  const obj = value;
  const keys2 = Object.keys(obj).sort();
  return `{${keys2.map((k) => `${JSON.stringify(k)}:${stableStringify(obj[k])}`).join(",")}}`;
}
function resolveConfig(config, scope = {}) {
  const ordered = [];
  for (const kind of CONFIG_LAYER_ORDER) {
    if (kind === ConfigLayerKind.GLOBAL) {
      ordered.push(["global", config.global]);
      continue;
    }
    const key = scope[SCOPE_KEY_FOR[kind]];
    if (!key) continue;
    const layer = config[kind]?.[key];
    if (layer) ordered.push([`${kind}:${key}`, layer]);
  }
  let merged = {};
  const applied = [];
  const unresolved = [];
  const seenExtends = /* @__PURE__ */ new Set();
  for (const [label, layer] of ordered) {
    if (!layer.inherit) {
      merged = {};
      applied.length = 0;
    }
    for (const extendsLabel of layer.extends) {
      if (seenExtends.has(extendsLabel)) continue;
      seenExtends.add(extendsLabel);
      const extra = lookupLayer(config, extendsLabel);
      if (!extra) {
        unresolved.push(extendsLabel);
        continue;
      }
      merged = deepMergeValues(merged, extra.values);
      applied.push(`extends:${extendsLabel}`);
    }
    merged = deepMergeValues(merged, layer.values);
    applied.push(label);
  }
  const normalizedScope = {
    chainId: scope.chainId ?? null,
    protocolId: scope.protocolId ?? null,
    customerId: scope.customerId ?? null,
    orgId: scope.orgId ?? null,
    tenantId: scope.tenantId ?? null,
    userId: scope.userId ?? null,
    txType: scope.txType ?? null,
    entityId: scope.entityId ?? null
  };
  const hash = stableHash(
    stableStringify({ values: merged, scope: normalizedScope, applied })
  );
  return {
    values: merged,
    scope: normalizedScope,
    appliedLayers: applied,
    unresolvedExtends: unresolved,
    hash
  };
}

// src/pipeline/document.ts
var BODY_SCOPES = ["customer", "org", "tenant", "user"];
var DEFAULT_BODY_SCOPE = "tenant";
var _seq = 0;
function newNodeId(prefix = "node") {
  _seq += 1;
  const rand = Math.random().toString(36).slice(2, 8);
  return `${prefix}_${Date.now().toString(36)}_${_seq}_${rand}`;
}
function createPipelineDoc(partial = {}) {
  const now = (/* @__PURE__ */ new Date()).toISOString();
  return {
    id: partial.id ?? newNodeId("pipeline"),
    name: partial.name ?? "Untitled pipeline",
    version: partial.version ?? "0.1.0",
    description: partial.description,
    targetTier: partial.targetTier ?? LatencyTier.CONFIRMED,
    dynamicStepBudget: partial.dynamicStepBudget ?? 8,
    parallelExecution: partial.parallelExecution ?? false,
    nodes: partial.nodes ?? [],
    // tx_graph is on by default so every pipeline bakes the render-ready
    // GraphScene when a `tx_graph_build` node is present (a harmless no-op
    // otherwise). Pass `{ tx_graph: false }` to opt out.
    featureFlags: partial.featureFlags ?? { tx_graph: true },
    // Left unset by default: the backend already defaults the body lane to the
    // shared tenant scope ({@link DEFAULT_BODY_SCOPE}), so an unset value avoids
    // writing a redundant `config.persistence` overlay. Set explicitly (e.g. via
    // the builder toolbar) to opt into `user`-private bodies or widen to org /
    // customer.
    bodyScope: partial.bodyScope,
    configOverlay: partial.configOverlay,
    injections: partial.injections ?? [],
    hierarchicalConfig: partial.hierarchicalConfig,
    scope: partial.scope,
    templateId: partial.templateId,
    createdAt: partial.createdAt ?? now,
    updatedAt: partial.updatedAt ?? now,
    workspaceId: partial.workspaceId,
    tenantId: partial.tenantId
  };
}
function uniqueNodeName(doc, base) {
  const taken = new Set(doc.nodes.map((n) => n.nodeName));
  if (!taken.has(base)) return base;
  let i = 2;
  while (taken.has(`${base}_${i}`)) i += 1;
  return `${base}_${i}`;
}
function topoSort(doc) {
  const byName = /* @__PURE__ */ new Map();
  for (const n of doc.nodes) byName.set(n.nodeName, n);
  const visited = /* @__PURE__ */ new Set();
  const temp = /* @__PURE__ */ new Set();
  const order = [];
  let cycle;
  const stack = [];
  const visit = (name) => {
    if (visited.has(name)) return true;
    if (temp.has(name)) {
      const idx = stack.indexOf(name);
      cycle = stack.slice(idx >= 0 ? idx : 0).concat(name);
      return false;
    }
    temp.add(name);
    stack.push(name);
    const node = byName.get(name);
    if (node) {
      for (const dep of node.needs) {
        if (!byName.has(dep)) continue;
        if (!visit(dep)) return false;
      }
    }
    stack.pop();
    temp.delete(name);
    visited.add(name);
    order.push(name);
    return true;
  };
  for (const n of doc.nodes) {
    if (!visit(n.nodeName)) return { order: [], cycle };
  }
  return { order };
}
function validatePipelineDoc(doc, options = {}) {
  const issues = [];
  if (doc.nodes.length === 0) {
    issues.push({ level: "warning", code: "empty_pipeline", message: "Pipeline has no steps yet." });
  }
  const seen = /* @__PURE__ */ new Set();
  const seenIds = /* @__PURE__ */ new Set();
  for (const node of doc.nodes) {
    if (node.nodeName.trim() === "") {
      issues.push({
        level: "error",
        code: "empty_node_name",
        message: `Step "${node.stepName}" has an empty node name.`,
        nodeId: node.id
      });
    }
    if (seen.has(node.nodeName)) {
      issues.push({
        level: "error",
        code: "duplicate_node_name",
        message: `Duplicate node name "${node.nodeName}".`,
        nodeId: node.id
      });
    }
    seen.add(node.nodeName);
    if (seenIds.has(node.id)) {
      issues.push({
        level: "error",
        code: "duplicate_node_id",
        message: `Duplicate node id "${node.id}" \u2014 "${node.nodeName}" shares it with another step.`,
        nodeId: node.id
      });
    }
    seenIds.add(node.id);
  }
  const names = new Set(doc.nodes.map((n) => n.nodeName));
  for (const node of doc.nodes) {
    for (const dep of node.needs) {
      if (!names.has(dep)) {
        issues.push({
          level: "error",
          code: "dangling_edge",
          message: `"${node.nodeName}" needs "${dep}" which is not in this pipeline.`,
          nodeId: node.id
        });
      }
    }
  }
  const { cycle } = topoSort(doc);
  if (cycle && cycle.length > 0) {
    issues.push({
      level: "error",
      code: "compile_error",
      message: `Pipeline has a cycle: ${cycle.join(" \u2192 ")}.`
    });
  }
  if (doc.dynamicStepBudget < 0) {
    issues.push({
      level: "error",
      code: "invalid_dynamic_step_budget",
      message: "Dynamic-step budget must be \u2265 0."
    });
  }
  const lookup = options.lookup;
  if (lookup) {
    for (const node of doc.nodes) {
      const meta = lookup(node.stepName, node.stepVersion);
      if (!meta) {
        issues.push({
          level: "warning",
          code: "unknown_step",
          message: `Step ${node.stepName}@${node.stepVersion} is not in the loaded registry \u2014 the server will reject it unless the catalog is stale.`,
          nodeId: node.id,
          field: "step_name"
        });
        continue;
      }
      for (const opt of meta.options) {
        if (opt.required && !(opt.name in node.options)) {
          issues.push({
            level: "error",
            code: "missing_required_option",
            message: `Step ${node.stepName} requires option "${opt.name}".`,
            nodeId: node.id,
            field: opt.name
          });
        }
      }
      if (meta.supportedTiers.length > 0 && !meta.supportedTiers.includes(doc.targetTier)) {
        issues.push({
          level: "warning",
          code: "tier_unsupported",
          message: `Step ${node.stepName} does not support tier "${doc.targetTier}"; it will be skipped at run time.`,
          nodeId: node.id
        });
      }
      for (const flag of meta.requiredFlags) {
        if (doc.featureFlags?.[flag] === false) {
          issues.push({
            level: "warning",
            code: "feature_flag_off",
            message: `Step ${node.stepName} requires feature flag "${flag}", which this pipeline disables; it will be skipped at run time.`,
            nodeId: node.id
          });
        }
      }
    }
  }
  return issues;
}
function isDeployablePipeline(doc, options = {}) {
  return validatePipelineDoc(doc, options).every((i) => i.level !== "error");
}
function findNode(doc, nodeId) {
  return doc.nodes.find((n) => n.id === nodeId);
}

// src/pipeline/mermaid.ts
function mermaidIds(doc) {
  const byNodeId = /* @__PURE__ */ new Map();
  const byName = /* @__PURE__ */ new Map();
  const taken = /* @__PURE__ */ new Set();
  for (const node of doc.nodes) {
    const base = `n_${node.nodeName.replace(/[^A-Za-z0-9_]/g, "_")}`;
    let id = base;
    let i = 2;
    while (taken.has(id)) {
      id = `${base}_${i}`;
      i += 1;
    }
    taken.add(id);
    byNodeId.set(node.id, id);
    if (!byName.has(node.nodeName)) byName.set(node.nodeName, id);
  }
  return { byNodeId, byName };
}
function escapeLabel(text) {
  return text.replace(/"/g, "&quot;");
}
function mermaidLabel(node) {
  return escapeLabel(`${node.nodeName}<br/>step: ${node.stepName}@${node.stepVersion}`);
}
function renderPipelineMermaid(doc, options = {}) {
  const direction = options.direction ?? "TD";
  const lines = [`flowchart ${direction}`];
  const { byNodeId, byName } = mermaidIds(doc);
  const enrichIds = [];
  for (const node of doc.nodes) {
    const id = byNodeId.get(node.id);
    if (id === void 0) continue;
    lines.push(`    ${id}["${mermaidLabel(node)}"]`);
    const meta = options.catalog?.get(`${node.stepName}@${node.stepVersion}`);
    const enrich = meta ? isEnrichmentStep(meta) : node.stepName.startsWith("enrich_") || node.stepName.endsWith("_enrich");
    if (enrich) enrichIds.push(id);
  }
  for (const node of doc.nodes) {
    const targetId = byNodeId.get(node.id);
    if (targetId === void 0) continue;
    for (const dep of node.needs) {
      const sourceId = byName.get(dep);
      if (sourceId === void 0) continue;
      lines.push(`    ${sourceId} --> ${targetId}`);
    }
  }
  if (enrichIds.length > 0) {
    lines.push("    classDef enrich fill:#fef3c7,stroke:#d97706,color:#92400e;");
    lines.push(`    class ${enrichIds.join(",")} enrich;`);
  }
  return lines.join("\n");
}

// src/pipeline/catalog.ts
var ALL_TIERS = [
  LatencyTier.MEMPOOL,
  LatencyTier.UNCONFIRMED,
  LatencyTier.CONFIRMED,
  LatencyTier.FINALIZED
];
var CONFIRMED_UP = [LatencyTier.CONFIRMED, LatencyTier.FINALIZED];
function seed(s) {
  const owner = { team: s.team, description: s.description };
  if (s.customerId) owner.customerId = s.customerId;
  return {
    name: s.name,
    version: "0.1.0",
    owner,
    supportedTiers: s.tiers ?? ALL_TIERS,
    needs: s.needs ?? [],
    failurePolicy: s.failurePolicy ?? FailurePolicy.FAIL_STEP,
    retryPolicy: s.retryPolicy ?? { ...DEFAULT_RETRY_POLICY },
    requiredFlags: s.requiredFlags ?? [],
    options: s.options ?? [],
    category: s.category,
    contentHash: void 0
  };
}
var DEFAULT_STEP_CATALOG = [
  // Sources
  seed({ name: "ingress", category: StepCategory.SOURCE, team: "ingestion", description: "Entry point \u2014 accept a raw transaction payload.", failurePolicy: FailurePolicy.FAIL_PIPELINE }),
  seed({ name: "mempool_ingress", category: StepCategory.SOURCE, team: "ingestion", description: "Ingest unmined mempool transactions.", tiers: [LatencyTier.MEMPOOL], failurePolicy: FailurePolicy.FAIL_PIPELINE }),
  // Normalize
  seed({ name: "normalize", category: StepCategory.NORMALIZE, team: "ingestion", description: "Normalize a raw tx into the canonical model.", needs: ["ingress"], failurePolicy: FailurePolicy.FAIL_PIPELINE }),
  seed({ name: "normalize_btc", category: StepCategory.NORMALIZE, team: "ingestion", description: "Bitcoin-specific normalization." }),
  seed({ name: "normalize_solana", category: StepCategory.NORMALIZE, team: "ingestion", description: "Solana-specific normalization." }),
  seed({ name: "normalize_quicknode", category: StepCategory.NORMALIZE, team: "ingestion", description: "Normalize QuickNode provider payloads." }),
  seed({ name: "normalize_mempool", category: StepCategory.NORMALIZE, team: "ingestion", description: "Normalize mempool payloads.", tiers: [LatencyTier.MEMPOOL] }),
  // Decode & parse
  seed({
    name: "decode",
    category: StepCategory.DECODE,
    team: "protocols",
    description: "ABI-decode calldata + logs.",
    needs: ["normalize"],
    // Mirrors authe_tx_pipeline DecodeStep ctor flags (-> decode_transaction
    // DecodeOptions). SSOT for these defaults is the backend
    // `DecodeStep.catalog_option_defaults`; the deployed builder hydrates from
    // the backend registry (which reads that), so this fixture is the OFFLINE
    // mirror only — keep the two in sync. See the file header for why editing
    // these `default`s does NOT change a template/saved node's toggles.
    options: [
      { name: "decode_input", type: "boolean", default: true, description: "Decode the transaction input calldata." },
      { name: "decode_output", type: "boolean", default: true, description: "Decode call/return output data (needs an ABI-based decode)." },
      { name: "decode_events", type: "boolean", default: true, description: "Decode the emitted event logs." },
      { name: "decode_internal_txs", type: "boolean", default: true, description: "Decode internal (trace) transactions." },
      { name: "guess_abi", type: "boolean", default: true, description: "Heuristically guess a selectorless ABI when none is found." },
      { name: "follow_proxy", type: "boolean", default: true, description: "Resolve proxy \u2192 implementation when fetching the ABI." },
      { name: "use_default_abis", type: "boolean", default: true, description: "Fall back to bundled default ABIs (ERC-20/721/1155, \u2026)." },
      { name: "fetch_abi_from_s3", type: "boolean", default: true, description: "Fetch ABIs from the org/default S3 ABI store." },
      { name: "build_semantic", type: "boolean", default: true, description: "Build the semantic 'Proposal \u2192 \u2026 \u2192 Action' tree." },
      { name: "emit_metrics", type: "boolean", default: false, description: "Emit per-strategy decoder metrics (high cardinality; off by default)." },
      { name: "nested_semantic", type: "boolean", default: false, description: "Decode nested calldata via semantic strategies (sibling-hint / selector-routing / ancestor-frame / route-rules). Off by default." }
    ]
  }),
  seed({ name: "parse_proxy", category: StepCategory.DECODE, team: "protocols", description: "Resolve proxy \u2192 implementation for decoding." }),
  seed({ name: "parse_user_ops", category: StepCategory.DECODE, team: "protocols", description: "Parse ERC-4337 user operations." }),
  seed({ name: "parse_safe_execution", category: StepCategory.DECODE, team: "protocols", description: "Parse Gnosis Safe multisig executions." }),
  // Protocol
  seed({ name: "protocol_detect", category: StepCategory.PROTOCOL, team: "protocols", description: "Detect the protocol(s) a tx interacts with.", needs: ["decode"], requiredFlags: ["enable_protocol_detection"] }),
  seed({ name: "protocol_normalize", category: StepCategory.PROTOCOL, team: "protocols", description: "Normalize protocol-specific semantics.", needs: ["protocol_detect"], requiredFlags: ["enable_protocol_normalize"] }),
  // Enrichment (special visual treatment)
  seed({ name: "price_enrich", category: StepCategory.ENRICH, team: "enrichment", description: "Attach USD prices at tx time.", needs: ["normalize"], failurePolicy: FailurePolicy.DEGRADE }),
  seed({ name: "risk_enrich", category: StepCategory.ENRICH, team: "enrichment", description: "Compute risk scores / levels.", needs: ["normalize"], failurePolicy: FailurePolicy.DEGRADE }),
  seed({ name: "third_party_enrich", category: StepCategory.ENRICH, team: "enrichment", description: "Fan out to third-party enrichment providers.", failurePolicy: FailurePolicy.DEGRADE }),
  seed({ name: "enrich_address_catalog", category: StepCategory.ENRICH, team: "catalog", description: "Emit address-scope catalog enrichments (labels, entities, tags, groups).", needs: ["normalize"] }),
  seed({ name: "enrich_tx_catalog", category: StepCategory.ENRICH, team: "catalog", description: "Emit tx-scope catalog enrichments.", needs: ["normalize"] }),
  seed({ name: "enrich_asset_metadata", category: StepCategory.ENRICH, team: "tokens", description: "Resolve token definitions via the token SSOT (write-through) + emit reference-only enrichments + async address-exploration events.", needs: ["decode"], failurePolicy: FailurePolicy.DEGRADE }),
  seed({ name: "catalog_discover", category: StepCategory.ENRICH, team: "catalog", description: "Discover + quarantine new catalog candidates." }),
  seed({ name: "persist_discovery", category: StepCategory.ENRICH, team: "catalog", description: "Persist discovered catalog candidates." }),
  // Graph & entity
  seed({ name: "entity_resolve", category: StepCategory.GRAPH, team: "graph", description: "Resolve addresses to entities.", needs: ["normalize"] }),
  seed({ name: "cluster_assign", category: StepCategory.GRAPH, team: "graph", description: "Assign addresses to clusters.", needs: ["entity_resolve"] }),
  seed({ name: "graph_project", category: StepCategory.GRAPH, team: "graph", description: "Project tx into the entity/cluster graph." }),
  // Correlation / SAGA
  seed({ name: "correlate", category: StepCategory.CORRELATE, team: "correlation", description: "Correlate txs into multi-step sagas (callback/SAGA pattern).", needs: ["normalize"] }),
  seed({ name: "saga_reorg", category: StepCategory.CORRELATE, team: "correlation", description: "Reconcile saga state on reorg." }),
  // Persistence
  seed({ name: "persist", category: StepCategory.PERSIST, team: "persistence", description: "Persist the canonical tx." }),
  seed({ name: "persist_header", category: StepCategory.PERSIST, team: "persistence", description: "Persist tx header only (fast tiers).", tiers: [LatencyTier.MEMPOOL, LatencyTier.UNCONFIRMED, LatencyTier.CONFIRMED, LatencyTier.FINALIZED] }),
  seed({ name: "persist_body", category: StepCategory.PERSIST, team: "persistence", description: "Persist full tx body.", tiers: CONFIRMED_UP }),
  // Indexing
  seed({
    name: "metadata_extract",
    category: StepCategory.INDEX,
    team: "platform",
    description: "Normalize the decoded tx into metadata-registry EAV facts (path_id-keyed) and write them to the configured metadata sink. Soft-failing; no-op until a sink is wired.",
    tiers: CONFIRMED_UP,
    needs: ["decode"],
    failurePolicy: FailurePolicy.DEGRADE,
    requiredFlags: ["enable_metadata_registry"]
  }),
  seed({ name: "index_project", category: StepCategory.INDEX, team: "platform", description: "Project configurable metadata paths into custom search indexes.", requiredFlags: ["enable_index_projections"], options: [{ name: "index_name", type: "string", required: true, description: "Target search index." }] }),
  // Triggers
  seed({ name: "trigger_evaluate", category: StepCategory.TRIGGER, team: "platform", description: "Evaluate declarative trigger rules + dispatch callbacks.", needs: ["risk_enrich"] }),
  // Reorg & finality
  seed({ name: "reorg_detector", category: StepCategory.REORG, team: "ingestion", description: "Detect reorgs and emit correction events.", tiers: [LatencyTier.UNCONFIRMED, LatencyTier.CONFIRMED] }),
  seed({ name: "finality_promote", category: StepCategory.REORG, team: "ingestion", description: "Promote a tx to a higher finality tier.", tiers: CONFIRMED_UP }),
  // ML
  seed({ name: "ml_tx_summary", category: StepCategory.ML, team: "ml", description: "LLM-generated transaction summary.", failurePolicy: FailurePolicy.DEGRADE, requiredFlags: ["ml_tx_summary"] }),
  seed({ name: "ml_risk_rescore", category: StepCategory.ML, team: "ml", description: "ML model re-scoring of risk.", failurePolicy: FailurePolicy.DEGRADE, requiredFlags: ["ml_risk_rescore"] }),
  // Quota
  seed({ name: "quota_check", category: StepCategory.QUOTA, team: "platform", description: "Check plan quotas before expensive steps.", failurePolicy: FailurePolicy.FAIL_STEP }),
  seed({ name: "summary", category: StepCategory.INDEX, team: "platform", description: "Build a human-readable tx summary.", requiredFlags: ["enable_summaries"] })
];
function indexCatalog(catalog) {
  return new Map(catalog.map((s) => [stepRefId(s), s]));
}
function findStep(catalog, name, version) {
  if (version) return catalog.find((s) => s.name === name && s.version === version);
  return catalog.find((s) => s.name === name);
}
function groupByCategory(catalog) {
  const groups = /* @__PURE__ */ new Map();
  for (const step of catalog) {
    const bucket = groups.get(step.category) ?? [];
    bucket.push(step);
    groups.set(step.category, bucket);
  }
  return [...groups.entries()].map(([category, steps]) => ({ category, steps }));
}

// src/pipeline/injection.ts
var StepInjectionScope = {
  ORG: "org",
  TENANT: "tenant",
  USER: "user"
};
var STEP_INJECTION_SCOPE_ORDER = [
  StepInjectionScope.ORG,
  StepInjectionScope.TENANT,
  StepInjectionScope.USER
];
var STEP_INJECTION_SCOPE_LABEL = {
  [StepInjectionScope.ORG]: "Org",
  [StepInjectionScope.TENANT]: "Tenant",
  [StepInjectionScope.USER]: "User"
};
function scopeRank(scope) {
  return STEP_INJECTION_SCOPE_ORDER.indexOf(scope);
}
function injectedNodeName(inj) {
  return inj.nodeName ?? inj.stepName;
}
function effectiveInjections(injections) {
  const winners = /* @__PURE__ */ new Map();
  for (const inj of injections) {
    if (inj.enabled === false) continue;
    const name = injectedNodeName(inj);
    const current = winners.get(name);
    if (!current || scopeRank(inj.scope) >= scopeRank(current.scope)) {
      winners.set(name, inj);
    }
  }
  return [...winners.values()].sort((a, b) => scopeRank(a.scope) - scopeRank(b.scope));
}
function shadowedInjections(injections) {
  const winners = new Set(effectiveInjections(injections));
  return injections.filter((inj) => inj.enabled !== false && !winners.has(inj));
}
function injectionShadowedBy(injections) {
  const winnerByName = /* @__PURE__ */ new Map();
  for (const inj of effectiveInjections(injections)) winnerByName.set(injectedNodeName(inj), inj);
  const out = /* @__PURE__ */ new Map();
  for (const inj of shadowedInjections(injections)) {
    const winner = winnerByName.get(injectedNodeName(inj));
    if (winner) out.set(inj.id, winner);
  }
  return out;
}
function offsetPosition(base, index) {
  const x = (base?.x ?? 120) + 280;
  const y = (base?.y ?? 80) + index * 40;
  return { x, y };
}
function applyStepInjections(base, injections) {
  const resolved = effectiveInjections(injections);
  if (resolved.length === 0) return base;
  const doc = {
    ...base,
    nodes: base.nodes.map((n) => ({ ...n, needs: [...n.needs] }))
  };
  resolved.forEach((inj, i) => {
    const desiredName = injectedNodeName(inj);
    const existing = doc.nodes.find((n) => n.nodeName === desiredName);
    const anchor = inj.anchor ?? { kind: "end" };
    const anchorNode = anchor.kind === "after" || anchor.kind === "before" ? doc.nodes.find((n) => n.nodeName === anchor.nodeName) : void 0;
    const anchorMissing = (anchor.kind === "after" || anchor.kind === "before") && !anchorNode;
    let needs;
    if (inj.needs) {
      needs = [...inj.needs];
    } else if (anchorMissing || anchor.kind === "end") {
      needs = leafNodeNames(doc);
    } else if (anchor.kind === "after") {
      needs = [anchor.nodeName];
    } else if (anchor.kind === "before") {
      needs = anchorNode ? [...anchorNode.needs] : [];
    } else {
      needs = [];
    }
    if (existing) {
      existing.stepName = inj.stepName;
      existing.stepVersion = inj.stepVersion ?? existing.stepVersion;
      existing.options = inj.options ?? existing.options;
      if (inj.needs) existing.needs = [...inj.needs];
      return;
    }
    const nodeName = uniqueNodeName(doc, desiredName);
    const node = {
      id: newNodeId("inject"),
      nodeName,
      stepName: inj.stepName,
      stepVersion: inj.stepVersion ?? "0.1.0",
      needs,
      options: inj.options ?? {},
      position: offsetPosition(doc.nodes[doc.nodes.length - 1]?.position, i)
    };
    if (anchor.kind === "before" && anchorNode) {
      const absorbed = new Set(node.needs);
      anchorNode.needs = [nodeName, ...anchorNode.needs.filter((dep) => !absorbed.has(dep))];
    }
    doc.nodes.push(node);
  });
  return doc;
}
function leafNodeNames(doc) {
  const referenced = /* @__PURE__ */ new Set();
  for (const n of doc.nodes) for (const dep of n.needs) referenced.add(dep);
  const leaves = doc.nodes.filter((n) => !referenced.has(n.nodeName)).map((n) => n.nodeName);
  return leaves.length > 0 ? leaves : doc.nodes.map((n) => n.nodeName);
}

// src/pipeline/wire.ts
function pick(raw, ...keys2) {
  for (const key of keys2) {
    if (raw[key] !== void 0 && raw[key] !== null) return raw[key];
  }
  return void 0;
}
function asTier(value) {
  const tier = String(value ?? "").toLowerCase();
  return LATENCY_TIER_VALUES.includes(tier) ? tier : LatencyTier.CONFIRMED;
}
var LATENCY_TIER_VALUES = Object.values(LatencyTier);
function asCategory(value) {
  const cat = String(value ?? "").toLowerCase();
  return Object.values(StepCategory).includes(cat) ? cat : StepCategory.CUSTOM;
}
function asFailurePolicy(value) {
  const fp = String(value ?? "").toLowerCase();
  return Object.values(FailurePolicy).includes(fp) ? fp : FailurePolicy.FAIL_STEP;
}
function asStatus(value) {
  const s = String(value ?? "").toLowerCase();
  return Object.values(StepStatus).includes(s) ? s : StepStatus.OK;
}
function asBodyScope(value) {
  const s = String(value ?? "").toLowerCase();
  return BODY_SCOPES.includes(s) ? s : void 0;
}
function asInjectionScope(value) {
  const s = String(value ?? "").toLowerCase();
  return STEP_INJECTION_SCOPE_ORDER.includes(s) ? s : StepInjectionScope.USER;
}
function anchorToWire(anchor) {
  if (!anchor) return void 0;
  if (anchor.kind === "after" || anchor.kind === "before") {
    return { kind: anchor.kind, node_name: anchor.nodeName };
  }
  return { kind: anchor.kind };
}
function anchorFromWire(raw) {
  if (!raw || typeof raw !== "object") return void 0;
  const w = raw;
  const kind = String(w.kind ?? "");
  if (kind === "after" || kind === "before") {
    const nodeName = pick(w, "node_name", "nodeName");
    return nodeName === void 0 ? void 0 : { kind, nodeName };
  }
  if (kind === "start" || kind === "end") return { kind };
  return void 0;
}
function injectionToWire(inj) {
  const wire = { id: inj.id, scope: inj.scope, step_name: inj.stepName };
  if (inj.stepVersion !== void 0) wire.step_version = inj.stepVersion;
  if (inj.nodeName !== void 0) wire.node_name = inj.nodeName;
  if (inj.needs !== void 0) wire.needs = inj.needs;
  if (inj.options !== void 0) wire.options = inj.options;
  if (inj.enabled !== void 0) wire.enabled = inj.enabled;
  const anchor = anchorToWire(inj.anchor);
  if (anchor) wire.anchor = anchor;
  return wire;
}
function injectionFromWire(raw) {
  const inj = {
    id: pick(raw, "id") ?? newNodeId("inject"),
    scope: asInjectionScope(pick(raw, "scope")),
    stepName: pick(raw, "step_name", "stepName") ?? ""
  };
  const stepVersion = pick(raw, "step_version", "stepVersion");
  if (stepVersion !== void 0) inj.stepVersion = stepVersion;
  const nodeName = pick(raw, "node_name", "nodeName");
  if (nodeName !== void 0) inj.nodeName = nodeName;
  const needs = pick(raw, "needs");
  if (needs !== void 0) inj.needs = needs;
  const options = pick(raw, "options");
  if (options !== void 0) inj.options = options;
  const enabled = pick(raw, "enabled");
  if (enabled !== void 0) inj.enabled = enabled;
  const anchor = anchorFromWire(raw.anchor);
  if (anchor) inj.anchor = anchor;
  return inj;
}
function docToWire(doc) {
  const idByName = new Map(doc.nodes.map((n) => [n.nodeName, n.id]));
  return {
    id: doc.id,
    name: doc.name,
    description: doc.description ?? null,
    // The builder tracks a human semver in `version`; the backend doc carries an
    // integer `doc_version`. Keep the semver round-trippable in `metadata`.
    doc_version: 1,
    target_tier: doc.targetTier,
    parallel_execution: doc.parallelExecution,
    dynamic_step_budget: doc.dynamicStepBudget,
    nodes: doc.nodes.map((n) => nodeToWire(n, idByName)),
    // Inline pipeline-scoped config overlay. Feature flags here are folded into
    // the run's resolved `feature_flags` (the gate `tx_graph_build` and other
    // flag-gated steps consult), so a template can ship its required flags.
    // `persistence.body_scope` selects the lane the persist step writes each
    // transaction body to (see commons `scoped_body_prefix`).
    // `configOverlay` carries the keys the builder has no editor for (e.g. a
    // template's `batch` policy) and is written first so the two modelled keys
    // below stay authoritative for their own slots.
    config: {
      ...doc.configOverlay ?? {},
      ...doc.featureFlags && Object.keys(doc.featureFlags).length > 0 ? { feature_flags: { ...doc.featureFlags } } : {},
      ...doc.bodyScope ? { persistence: { body_scope: doc.bodyScope } } : {}
    },
    metadata: {
      fe_version: doc.version,
      scope: doc.scope ?? null,
      created_at: doc.createdAt ?? null,
      updated_at: doc.updatedAt ?? null,
      workspace_id: doc.workspaceId ?? null,
      tenant_id: doc.tenantId ?? null,
      // The template this pipeline was instantiated from — lets the run console
      // surface that template's ready-made run samples. Omitted when absent.
      ...doc.templateId ? { template_id: doc.templateId } : {},
      // Org/tenant/user step injections ride along in `metadata` (the backend
      // round-trips it verbatim) so the builder's "Step injection" panel
      // survives save/load. Omitted when empty to avoid spurious diffs.
      ...doc.injections && doc.injections.length > 0 ? { injections: doc.injections.map(injectionToWire) } : {},
      // The layered config authored in the builder's Config tab. Like
      // `injections` it rides in `metadata`, which the backend round-trips
      // verbatim, so authoring survives save/load. Omitted when absent.
      ...doc.hierarchicalConfig ? { hierarchical_config: doc.hierarchicalConfig } : {}
    }
  };
}
function nodeToWire(node, idByName) {
  return {
    id: node.id,
    name: node.nodeName,
    step_name: node.stepName,
    step_version: node.stepVersion,
    // Edges out by node id (fall back to the raw value so a genuinely dangling
    // dep still surfaces server-side rather than being silently dropped).
    needs: node.needs.map((dep) => idByName.get(dep) ?? dep),
    options: node.options,
    enabled: true,
    ui: { x: node.position.x, y: node.position.y }
  };
}
function docFromWire(raw) {
  const metadata = raw.metadata ?? {};
  const rawNodes = pick(raw, "nodes") ?? [];
  const nameById = /* @__PURE__ */ new Map();
  for (const rn of rawNodes) {
    const id = pick(rn, "id");
    if (id === void 0) continue;
    nameById.set(id, pick(rn, "name", "nodeName") ?? pick(rn, "step_name", "stepName") ?? id);
  }
  const config = pick(raw, "config") ?? {};
  const featureFlags = pick(config, "feature_flags", "featureFlags") ?? void 0;
  const persistence = pick(config, "persistence") ?? {};
  const bodyScope = asBodyScope(pick(persistence, "body_scope", "bodyScope"));
  const configOverlay = Object.fromEntries(
    Object.entries(config).filter(
      ([key]) => !["feature_flags", "featureFlags", "persistence"].includes(key)
    )
  );
  const rawInjections = pick(metadata, "injections") ?? pick(raw, "injections");
  return createPipelineDoc({
    id: pick(raw, "id"),
    name: pick(raw, "name"),
    version: pick(metadata, "fe_version") ?? pick(raw, "version") ?? "0.1.0",
    description: pick(raw, "description") ?? void 0,
    targetTier: asTier(pick(raw, "target_tier", "targetTier")),
    dynamicStepBudget: pick(raw, "dynamic_step_budget", "dynamicStepBudget") ?? 8,
    parallelExecution: pick(raw, "parallel_execution", "parallelExecution") ?? false,
    // `{}` — not `undefined` — when the payload carries no `feature_flags`.
    // `createPipelineDoc` reads `undefined` as "brand-new doc" and stamps its
    // `{ tx_graph: true }` default, so hydrating an externally-authored pipeline
    // that never mentioned a flag and pressing Save wrote `tx_graph: true` into
    // its config. That is not a no-op: an ABSENT flag resolves from layered
    // config at run time (env / tenant / run overlay), while a flag pinned at the
    // pipeline layer overrides all of them. Absence is a value here, so it
    // round-trips as one — `docToWire` omits an empty map again.
    featureFlags: featureFlags ?? {},
    bodyScope,
    configOverlay: Object.keys(configOverlay).length > 0 ? configOverlay : void 0,
    injections: rawInjections ? rawInjections.map(injectionFromWire) : void 0,
    nodes: rawNodes.map((rn) => nodeFromWire(rn, nameById)),
    hierarchicalConfig: pick(metadata, "hierarchical_config", "hierarchicalConfig") ?? pick(raw, "hierarchical_config", "hierarchicalConfig"),
    scope: pick(metadata, "scope") ?? pick(raw, "scope"),
    templateId: pick(metadata, "template_id", "templateId") ?? pick(raw, "template_id", "templateId"),
    createdAt: pick(metadata, "created_at") ?? pick(raw, "created_at", "createdAt"),
    updatedAt: pick(metadata, "updated_at") ?? pick(raw, "updated_at", "updatedAt"),
    workspaceId: pick(metadata, "workspace_id") ?? pick(raw, "workspace_id", "workspaceId"),
    tenantId: pick(metadata, "tenant_id") ?? pick(raw, "tenant_id", "tenantId")
  });
}
function nodeFromWire(raw, nameById) {
  const stepName = pick(raw, "step_name", "stepName") ?? "";
  const ui = pick(raw, "ui") ?? {};
  const pos = pick(raw, "position") ?? { x: ui.x ?? 0, y: ui.y ?? 0 };
  const rawNeeds = pick(raw, "needs") ?? [];
  return {
    // A minted id, not the step name: two nodes running the same step in a
    // payload whose nodes carry no id (a hand seed / pre-cutover doc) both
    // landed on the same canvas id, and one id means one node — selecting,
    // moving or deleting either hit both.
    id: pick(raw, "id") ?? newNodeId(),
    nodeName: pick(raw, "name", "nodeName") ?? stepName,
    stepName,
    stepVersion: pick(raw, "step_version", "stepVersion") ?? "0.1.0",
    needs: rawNeeds.map((dep) => nameById.get(dep) ?? dep),
    options: pick(raw, "options") ?? {},
    position: { x: pos.x ?? 0, y: pos.y ?? 0 }
  };
}
function stepMetadataFromWire(raw) {
  return {
    name: pick(raw, "name") ?? "",
    version: pick(raw, "version") ?? "0.1.0",
    owner: {
      team: pick(raw, "owner_team", "ownerTeam") ?? "unassigned",
      description: pick(raw, "description") ?? void 0
    },
    supportedTiers: (pick(raw, "supported_tiers", "supportedTiers") ?? []).map(asTier),
    needs: pick(raw, "needs") ?? [],
    failurePolicy: asFailurePolicy(pick(raw, "failure_policy", "failurePolicy")),
    retryPolicy: { ...DEFAULT_RETRY_POLICY },
    requiredFlags: pick(raw, "required_flags", "requiredFlags") ?? [],
    options: (pick(raw, "options") ?? []).map((o) => ({
      name: pick(o, "name") ?? "",
      type: pick(o, "type") ?? "string",
      required: pick(o, "required") ?? false,
      default: pick(o, "default"),
      description: pick(o, "description") ?? void 0,
      choices: pick(o, "enum_values", "choices")
    })),
    category: asCategory(pick(raw, "category")),
    custom: pick(raw, "custom") ?? false,
    runtimeId: pick(raw, "code_runtime_id", "runtimeId") ?? void 0
  };
}
function catalogFromWire(raw) {
  const steps = pick(raw, "steps") ?? (Array.isArray(raw) ? raw : []);
  return steps.map(stepMetadataFromWire);
}
function templateNodeFromWire(raw) {
  const node = {
    stepName: pick(raw, "step_name", "stepName") ?? ""
  };
  const stepVersion = pick(raw, "step_version", "stepVersion");
  if (stepVersion) node.stepVersion = stepVersion;
  const nodeName = pick(raw, "node_name", "nodeName");
  if (nodeName) node.nodeName = nodeName;
  const needs = pick(raw, "needs");
  if (needs && needs.length > 0) node.needs = needs;
  const options = pick(raw, "options");
  if (options && Object.keys(options).length > 0) node.options = options;
  return node;
}
function txSpecFromWire(raw) {
  const spec = {
    chainId: pick(raw, "chain_id", "chainId") ?? "",
    txHash: pick(raw, "tx_hash", "txHash") ?? ""
  };
  const label = pick(raw, "label");
  if (label) spec.label = label;
  return spec;
}
function sampleFromWire(raw) {
  const sample = {
    id: pick(raw, "id") ?? "",
    name: pick(raw, "name") ?? "",
    sourceKind: pick(raw, "source_kind", "sourceKind") ?? "tx_hashes"
  };
  const description = pick(raw, "description");
  if (description) sample.description = description;
  const txSpecs = pick(raw, "tx_specs", "txSpecs");
  if (txSpecs && txSpecs.length > 0) sample.txSpecs = txSpecs.map(txSpecFromWire);
  const chainId = pick(raw, "chain_id", "chainId");
  if (chainId) sample.chainId = chainId;
  const txHashes = pick(raw, "tx_hashes", "txHashes");
  if (txHashes && txHashes.length > 0) sample.txHashes = txHashes;
  const address = pick(raw, "address");
  if (address) sample.address = address;
  const selector = pick(raw, "selector");
  if (selector) sample.selector = selector;
  const transactionTypes = pick(raw, "transaction_types", "transactionTypes");
  if (transactionTypes) sample.transactionTypes = transactionTypes;
  const count = pick(raw, "count");
  if (count !== void 0) sample.count = count;
  return sample;
}
function templateFromWire(raw) {
  const tmpl = {
    id: pick(raw, "id") ?? "",
    name: pick(raw, "name") ?? "",
    description: pick(raw, "description") ?? "",
    nodes: (pick(raw, "nodes") ?? []).map(templateNodeFromWire)
  };
  const category = pick(raw, "category");
  if (category) tmpl.category = category;
  const targetTier = pick(raw, "target_tier", "targetTier");
  if (targetTier !== void 0) tmpl.targetTier = asTier(targetTier);
  const budget = pick(raw, "dynamic_step_budget", "dynamicStepBudget");
  if (budget !== void 0) tmpl.dynamicStepBudget = budget;
  const flags = pick(raw, "feature_flags", "featureFlags");
  if (flags && Object.keys(flags).length > 0) tmpl.featureFlags = flags;
  const config = pick(raw, "config");
  if (config && Object.keys(config).length > 0) tmpl.config = config;
  const samples = pick(raw, "samples");
  if (samples && samples.length > 0) tmpl.samples = samples.map(sampleFromWire);
  return tmpl;
}
function templatesFromWire(raw) {
  const list = Array.isArray(raw) ? raw : pick(raw, "templates") ?? [];
  return list.map(templateFromWire);
}
var ISSUE_LEVELS = /* @__PURE__ */ new Set(["error", "warning", "info"]);
function issueFromWire(raw) {
  const level = String(pick(raw, "level") ?? "error").toLowerCase();
  const issue = {
    level: ISSUE_LEVELS.has(level) ? level : "error",
    message: pick(raw, "message") ?? "",
    nodeId: pick(raw, "node_id", "nodeId") ?? void 0
  };
  const code = pick(raw, "code");
  if (code) issue.code = code;
  const field = pick(raw, "field");
  if (field) issue.field = field;
  return issue;
}
function issuesFromWire(raw) {
  const list = Array.isArray(raw) ? raw : raw.issues ?? [];
  return list.map(issueFromWire);
}
function decisionFromWire(raw) {
  if (!raw || typeof raw !== "object") return void 0;
  const w = raw;
  const reason = pick(w, "reason");
  if (reason === void 0) return void 0;
  return {
    reason,
    summary: pick(w, "summary") ?? "",
    detail: pick(w, "detail") ?? {}
  };
}
function outcomeFromWire(raw) {
  const outcome = {
    nodeName: pick(raw, "node_name", "nodeName") ?? "",
    stepName: pick(raw, "step_name", "stepName") ?? "",
    stepVersion: pick(raw, "step_version", "stepVersion") ?? "",
    status: asStatus(pick(raw, "status")),
    error: pick(raw, "error") ?? null,
    enrichmentCount: pick(raw, "enrichment_count", "enrichmentCount") ?? 0,
    attempts: pick(raw, "attempts") ?? 1,
    nextSteps: []
  };
  const durationMs = pick(raw, "duration_ms", "durationMs");
  if (typeof durationMs === "number" && Number.isFinite(durationMs)) {
    outcome.durationMs = durationMs;
  }
  const decision = decisionFromWire(raw.decision) ?? (pick(raw, "decision_reason", "decisionReason") !== void 0 ? {
    reason: pick(raw, "decision_reason", "decisionReason"),
    summary: pick(raw, "decision_summary", "decisionSummary") ?? "",
    detail: pick(raw, "decision_detail", "decisionDetail") ?? {}
  } : void 0);
  if (decision !== void 0) outcome.decision = decision;
  return outcome;
}
function runResultFromWire(raw) {
  const outcomes = (pick(raw, "outcomes") ?? []).map(outcomeFromWire);
  return {
    outcomes,
    dynamicStepsRun: pick(raw, "dynamic_steps_run", "dynamicStepsRun") ?? 0,
    output: raw
  };
}

// src/pipeline/template-catalog.generated.json
var template_catalog_generated_default = {
  templates: [
    {
      category: "EVM",
      config: {},
      description: "Ingest \u2192 normalize \u2192 decode \u2192 protocol detect + normalize \u2192 metadata facts \u2192 enrich (price/risk/catalog) \u2192 graph \u2192 persist + index.",
      dynamic_step_budget: 8,
      feature_flags: {
        enable_grouping: true,
        enable_instance_lifecycle: true,
        enable_metadata_registry: true,
        enable_protocol_detection: true,
        enable_protocol_normalize: true,
        tx_graph: true
      },
      id: "evm-standard",
      name: "EVM standard enrichment",
      nodes: [
        {
          needs: [],
          node_name: null,
          options: {},
          step_name: "ingress",
          step_version: null
        },
        {
          needs: [
            "ingress"
          ],
          node_name: null,
          options: {},
          step_name: "normalize_moralis",
          step_version: null
        },
        {
          needs: [
            "normalize_moralis"
          ],
          node_name: null,
          options: {},
          step_name: "decode",
          step_version: "0.2.0"
        },
        {
          needs: [
            "decode"
          ],
          node_name: null,
          options: {},
          step_name: "protocol_detect",
          step_version: "0.1.0"
        },
        {
          needs: [
            "protocol_detect"
          ],
          node_name: null,
          options: {},
          step_name: "protocol_normalize",
          step_version: "0.1.0"
        },
        {
          needs: [
            "protocol_normalize",
            "price_enrich",
            "instance_lifecycle"
          ],
          node_name: null,
          options: {},
          step_name: "metadata_extract",
          step_version: null
        },
        {
          needs: [
            "protocol_normalize"
          ],
          node_name: null,
          options: {},
          step_name: "instance_lifecycle",
          step_version: null
        },
        {
          needs: [
            "protocol_detect"
          ],
          node_name: null,
          options: {},
          step_name: "price_enrich",
          step_version: null
        },
        {
          needs: [
            "normalize_moralis"
          ],
          node_name: null,
          options: {},
          step_name: "risk_enrich",
          step_version: null
        },
        {
          needs: [
            "normalize_moralis"
          ],
          node_name: null,
          options: {},
          step_name: "enrich_address_catalog",
          step_version: null
        },
        {
          needs: [
            "decode"
          ],
          node_name: null,
          options: {},
          step_name: "tx_graph_build",
          step_version: null
        },
        {
          needs: [
            "price_enrich",
            "risk_enrich",
            "enrich_address_catalog",
            "tx_graph_build"
          ],
          node_name: null,
          options: {},
          step_name: "persist",
          step_version: "2.0.0"
        },
        {
          needs: [
            "persist"
          ],
          node_name: null,
          options: {},
          step_name: "index_project",
          step_version: null
        },
        {
          needs: [
            "decode"
          ],
          node_name: null,
          options: {},
          step_name: "group",
          step_version: "0.1.0"
        },
        {
          needs: [
            "instance_lifecycle",
            "group"
          ],
          node_name: null,
          options: {},
          step_name: "context_signal_project",
          step_version: "0.1.0"
        },
        {
          needs: [
            "context_signal_project",
            "protocol_detect",
            "risk_enrich",
            "price_enrich",
            "enrich_address_catalog"
          ],
          node_name: null,
          options: {},
          step_name: "trigger_evaluate",
          step_version: "0.1.0"
        }
      ],
      samples: [],
      target_tier: "confirmed"
    },
    {
      category: "Solana",
      config: {},
      description: "Solana family-dispatched pipeline: ingress \u2192 normalize (Solana) \u2192 decode \u2192 tx_action \u2192 graph \u2192 persist. The decode step routes by chain family to the decode_transaction.solana coordinator, which builds the SemanticTree from the parsed instructions + program registry + account model (no EVM ABI/proxy stack). tx_action then classifies the headline CanonicalAction (Jupiter swap / SPL / native transfer) from that tree, and tx_graph_build renders the base58 scene. The blueprint for adding any non-EVM chain via a ChainPipelineProfile.",
      dynamic_step_budget: 8,
      feature_flags: {
        enable_instance_lifecycle: true,
        enable_metadata_registry: true,
        enable_protocol_detection: true,
        enable_protocol_normalize: true,
        enable_tx_semantic: true,
        tx_graph: true
      },
      id: "solana-standard",
      name: "Solana standard enrichment",
      nodes: [
        {
          needs: [],
          node_name: null,
          options: {},
          step_name: "ingress",
          step_version: null
        },
        {
          needs: [
            "ingress"
          ],
          node_name: null,
          options: {},
          step_name: "normalize_solana",
          step_version: null
        },
        {
          needs: [
            "normalize_solana"
          ],
          node_name: null,
          options: {
            build_semantic: true
          },
          step_name: "decode",
          step_version: "0.2.0"
        },
        {
          needs: [
            "decode"
          ],
          node_name: null,
          options: {},
          step_name: "tx_action",
          step_version: null
        },
        {
          needs: [
            "decode"
          ],
          node_name: null,
          options: {},
          step_name: "tx_graph_build",
          step_version: null
        },
        {
          needs: [
            "decode"
          ],
          node_name: null,
          options: {},
          step_name: "protocol_detect",
          step_version: "0.1.0"
        },
        {
          needs: [
            "protocol_detect"
          ],
          node_name: null,
          options: {},
          step_name: "protocol_normalize",
          step_version: "0.1.0"
        },
        {
          needs: [
            "protocol_detect"
          ],
          node_name: null,
          options: {},
          step_name: "price_enrich",
          step_version: null
        },
        {
          needs: [
            "protocol_normalize"
          ],
          node_name: null,
          options: {},
          step_name: "instance_lifecycle",
          step_version: null
        },
        {
          needs: [
            "protocol_normalize",
            "price_enrich",
            "instance_lifecycle"
          ],
          node_name: null,
          options: {},
          step_name: "metadata_extract",
          step_version: null
        },
        {
          needs: [
            "tx_action",
            "tx_graph_build",
            "price_enrich"
          ],
          node_name: null,
          options: {},
          step_name: "persist",
          step_version: "2.0.0"
        },
        {
          needs: [
            "persist"
          ],
          node_name: null,
          options: {},
          step_name: "index_project",
          step_version: null
        },
        {
          needs: [
            "instance_lifecycle"
          ],
          node_name: null,
          options: {},
          step_name: "context_signal_project",
          step_version: "0.1.0"
        },
        {
          needs: [
            "context_signal_project",
            "tx_action",
            "protocol_detect"
          ],
          node_name: null,
          options: {},
          step_name: "trigger_evaluate",
          step_version: "0.1.0"
        }
      ],
      samples: [],
      target_tier: "confirmed"
    },
    {
      category: "Streaming",
      config: {},
      description: "Low-latency preliminary results from the mempool with minimal enrichment.",
      dynamic_step_budget: 4,
      feature_flags: {
        tx_graph: true
      },
      id: "mempool-fast-lane",
      name: "Mempool fast lane",
      nodes: [
        {
          needs: [],
          node_name: null,
          options: {},
          step_name: "mempool_ingress",
          step_version: null
        },
        {
          needs: [
            "mempool_ingress"
          ],
          node_name: null,
          options: {},
          step_name: "normalize_mempool",
          step_version: null
        },
        {
          needs: [
            "normalize_mempool"
          ],
          node_name: null,
          options: {},
          step_name: "risk_enrich",
          step_version: null
        },
        {
          needs: [
            "normalize_mempool"
          ],
          node_name: null,
          options: {},
          step_name: "tx_graph_build",
          step_version: null
        },
        {
          needs: [
            "normalize_mempool",
            "tx_graph_build"
          ],
          node_name: null,
          options: {},
          step_name: "persist",
          step_version: "2.0.0"
        }
      ],
      samples: [],
      target_tier: "mempool"
    },
    {
      category: "Reorg",
      config: {},
      description: "Confirmed-tier pipeline with reorg detection and finality promotion.",
      dynamic_step_budget: 8,
      feature_flags: {
        tx_graph: true
      },
      id: "reorg-aware",
      name: "Reorg-aware confirmed",
      nodes: [
        {
          needs: [],
          node_name: null,
          options: {},
          step_name: "ingress",
          step_version: null
        },
        {
          needs: [
            "ingress"
          ],
          node_name: null,
          options: {},
          step_name: "normalize_moralis",
          step_version: null
        },
        {
          needs: [
            "normalize_moralis"
          ],
          node_name: null,
          options: {},
          step_name: "reorg_detector",
          step_version: null
        },
        {
          needs: [
            "normalize_moralis"
          ],
          node_name: null,
          options: {},
          step_name: "finality_promote",
          step_version: null
        },
        {
          needs: [
            "normalize_moralis"
          ],
          node_name: null,
          options: {},
          step_name: "tx_graph_build",
          step_version: null
        },
        {
          needs: [
            "finality_promote",
            "tx_graph_build"
          ],
          node_name: null,
          options: {},
          step_name: "persist",
          step_version: "2.0.0"
        },
        {
          needs: [
            "finality_promote"
          ],
          node_name: null,
          options: {},
          step_name: "trigger_evaluate",
          step_version: "0.1.0"
        }
      ],
      samples: [],
      target_tier: "confirmed"
    },
    {
      category: "Protocol",
      config: {},
      description: "Protocol pipeline for Chainlink CCIP ccipSend on Ethereum: ingress \u2192 normalize (Moralis) \u2192 decode \u2192 parse user-ops / Safe / proxy \u2192 build graph \u2192 persist. Mirrors the ccip_eth definition used by the local pipeline-run harness, with the render-ready cross-chain GraphScene baked in.",
      dynamic_step_budget: 8,
      feature_flags: {
        enable_grouping: true,
        enable_metadata_registry: true,
        enable_protocol_detection: true,
        enable_protocol_normalize: true,
        tx_graph: true
      },
      id: "chainlink-ccip-ethereum",
      name: "Chainlink CCIP (Ethereum)",
      nodes: [
        {
          needs: [],
          node_name: null,
          options: {},
          step_name: "ingress",
          step_version: "0.1.0"
        },
        {
          needs: [
            "ingress"
          ],
          node_name: null,
          options: {},
          step_name: "normalize_moralis",
          step_version: "0.1.0"
        },
        {
          needs: [
            "normalize_moralis"
          ],
          node_name: null,
          options: {
            build_semantic: true,
            decode_events: true,
            decode_input: true,
            decode_internal_txs: true,
            decode_output: true,
            fetch_abi_from_s3: true,
            follow_proxy: true,
            guess_abi: true,
            use_default_abis: true
          },
          step_name: "decode",
          step_version: "0.2.0"
        },
        {
          needs: [
            "decode"
          ],
          node_name: null,
          options: {},
          step_name: "protocol_detect",
          step_version: "0.1.0"
        },
        {
          needs: [
            "protocol_detect"
          ],
          node_name: null,
          options: {},
          step_name: "protocol_normalize",
          step_version: "0.1.0"
        },
        {
          needs: [
            "protocol_normalize"
          ],
          node_name: null,
          options: {},
          step_name: "metadata_extract",
          step_version: "0.1.0"
        },
        {
          needs: [
            "decode"
          ],
          node_name: null,
          options: {},
          step_name: "parse_user_ops",
          step_version: "0.1.0"
        },
        {
          needs: [
            "parse_user_ops"
          ],
          node_name: null,
          options: {},
          step_name: "parse_safe_execution",
          step_version: "0.1.0"
        },
        {
          needs: [
            "parse_safe_execution"
          ],
          node_name: null,
          options: {},
          step_name: "parse_proxy",
          step_version: "0.1.0"
        },
        {
          needs: [
            "parse_proxy"
          ],
          node_name: null,
          options: {},
          step_name: "context_proxy_write",
          step_version: "0.1.0"
        },
        {
          needs: [
            "decode"
          ],
          node_name: null,
          options: {},
          step_name: "tx_graph_build",
          step_version: "0.1.0"
        },
        {
          needs: [
            "parse_proxy"
          ],
          node_name: null,
          options: {},
          step_name: "reorg_detector",
          step_version: "0.1.0"
        },
        {
          needs: [
            "reorg_detector"
          ],
          node_name: null,
          options: {},
          step_name: "finality_promote",
          step_version: "0.1.0"
        },
        {
          needs: [
            "finality_promote",
            "tx_graph_build"
          ],
          node_name: null,
          options: {},
          step_name: "persist",
          step_version: "2.0.0"
        },
        {
          needs: [
            "parse_proxy"
          ],
          node_name: null,
          options: {},
          step_name: "group",
          step_version: "0.1.0"
        },
        {
          needs: [
            "group"
          ],
          node_name: null,
          options: {},
          step_name: "context_signal_project",
          step_version: "0.1.0"
        },
        {
          needs: [
            "context_signal_project",
            "protocol_detect"
          ],
          node_name: null,
          options: {},
          step_name: "trigger_evaluate",
          step_version: "0.1.0"
        }
      ],
      samples: [],
      target_tier: "confirmed"
    },
    {
      category: "Protocol",
      config: {},
      description: "Cross-chain CCIP grouping: decode the Ethereum source ccipSend (CCIPMessageSent on the 1.6 OnRamp) and its Base destination execution (ExecutionStateChanged on the OffRamp) \u2014 both carrying the same CCIP messageId \u2014 then run the tx_grouping engine so the two legs converge into a single bridge.ccip group with a confidence score + evidence (even ingested out of order). Persists the decoded transactions to the application tables and the TransactionGroup state + tx\u2194group overlays to the grouping tables. Each leg is classified by tx_semantic (tx_action) and the completing leg reduces the group's headline action (group_action), which is persisted onto the group row so the group view can say what happened. Mirrors the ccip_eth_base_grouping pipeline-run definition with --grouping ddb_aws.",
      dynamic_step_budget: 8,
      feature_flags: {
        enable_grouping: true,
        enable_metadata_registry: true,
        enable_protocol_detection: true,
        enable_protocol_normalize: true,
        enable_tx_semantic: true,
        tx_graph: true
      },
      id: "chainlink-ccip-cross-chain-grouping",
      name: "Chainlink CCIP cross-chain grouping",
      nodes: [
        {
          needs: [],
          node_name: null,
          options: {},
          step_name: "ingress",
          step_version: "0.1.0"
        },
        {
          needs: [
            "ingress"
          ],
          node_name: null,
          options: {},
          step_name: "normalize_moralis",
          step_version: "0.1.0"
        },
        {
          needs: [
            "normalize_moralis"
          ],
          node_name: null,
          options: {
            build_semantic: true,
            decode_events: true,
            decode_input: true,
            decode_internal_txs: true,
            decode_output: true,
            emit_metrics: false,
            fetch_abi_from_s3: true,
            follow_proxy: true,
            guess_abi: true,
            use_default_abis: true
          },
          step_name: "decode",
          step_version: "0.2.0"
        },
        {
          needs: [
            "decode"
          ],
          node_name: null,
          options: {},
          step_name: "protocol_detect",
          step_version: "0.1.0"
        },
        {
          needs: [
            "protocol_detect"
          ],
          node_name: null,
          options: {},
          step_name: "protocol_normalize",
          step_version: "0.1.0"
        },
        {
          needs: [
            "protocol_normalize"
          ],
          node_name: null,
          options: {},
          step_name: "metadata_extract",
          step_version: "0.1.0"
        },
        {
          needs: [
            "decode"
          ],
          node_name: null,
          options: {},
          step_name: "parse_user_ops",
          step_version: "0.1.0"
        },
        {
          needs: [
            "parse_user_ops"
          ],
          node_name: null,
          options: {},
          step_name: "parse_safe_execution",
          step_version: "0.1.0"
        },
        {
          needs: [
            "parse_safe_execution"
          ],
          node_name: null,
          options: {},
          step_name: "parse_proxy",
          step_version: "0.1.0"
        },
        {
          needs: [
            "parse_proxy"
          ],
          node_name: null,
          options: {},
          step_name: "context_proxy_write",
          step_version: "0.1.0"
        },
        {
          needs: [
            "decode"
          ],
          node_name: null,
          options: {},
          step_name: "tx_graph_build",
          step_version: "0.1.0"
        },
        {
          needs: [
            "parse_proxy"
          ],
          node_name: null,
          options: {},
          step_name: "reorg_detector",
          step_version: "0.1.0"
        },
        {
          needs: [
            "reorg_detector"
          ],
          node_name: null,
          options: {},
          step_name: "finality_promote",
          step_version: "0.1.0"
        },
        {
          needs: [
            "finality_promote",
            "tx_graph_build"
          ],
          node_name: null,
          options: {},
          step_name: "persist",
          step_version: "2.0.0"
        },
        {
          needs: [
            "decode"
          ],
          node_name: null,
          options: {},
          step_name: "tx_action",
          step_version: "0.1.0"
        },
        {
          needs: [
            "parse_proxy"
          ],
          node_name: null,
          options: {},
          step_name: "group",
          step_version: "0.1.0"
        },
        {
          needs: [
            "group"
          ],
          node_name: null,
          options: {},
          step_name: "group_action",
          step_version: "0.1.0"
        },
        {
          needs: [
            "group"
          ],
          node_name: null,
          options: {},
          step_name: "context_signal_project",
          step_version: "0.1.0"
        },
        {
          needs: [
            "context_signal_project",
            "protocol_detect"
          ],
          node_name: null,
          options: {},
          step_name: "trigger_evaluate",
          step_version: "0.1.0"
        }
      ],
      samples: [
        {
          address: null,
          chain_id: null,
          count: 2,
          description: "A single Chainlink CCIP message: the source ccipSend on Ethereum and its destination execution on Base. Grouping converges both legs into one bridge.ccip group.",
          id: "ccip-eth-base-message",
          name: "CCIP message: Ethereum \u2192 Base",
          selector: null,
          source_kind: "tx_specs",
          transaction_types: null,
          tx_hashes: [],
          tx_specs: [
            {
              chain_id: "0x1",
              label: "Ethereum source ccipSend",
              tx_hash: "0x0319d6769960690d637e0bac234272395e149ce35422169b85c3534f41a099bf"
            },
            {
              chain_id: "0x2105",
              label: "Base destination execution",
              tx_hash: "0x3858e79e42b937cc65355a6988acf80e3cbf0c6a13afe30b72f406e5f8cdcef3"
            }
          ]
        }
      ],
      target_tier: "confirmed"
    },
    {
      category: "Protocol",
      config: {},
      description: "Cross-chain Wormhole grouping across a multi-hop saga BSC \u2192 Ethereum \u2192 Base, carried by TWO guardian-signed VAAs. Each leg's composite VAA key (emitterChain + emitterAddress + sequence) is decoded \u2014 LogMessagePublished on the source Core Bridge, TransferRedeemed on the destination Token Bridge \u2014 then the tx_grouping engine converges the legs into bridge.wormhole groups with a confidence score + evidence (even ingested out of order). The Ethereum leg is a HUB: it redeems the inbound VAA from BSC AND publishes a new onward VAA to Base, so it is the DESTINATION of the first group and the SOURCE of the second \u2014 which is why WormholeScan lists multiple messages against that one transaction. Persists the decoded transactions to the application tables and the TransactionGroup state + tx\u2194group overlays to the grouping tables. Definition-driven (the wormhole ProtocolDefinition owns the composite cross-chain linkage).",
      dynamic_step_budget: 8,
      feature_flags: {
        enable_grouping: true,
        enable_metadata_registry: true,
        enable_protocol_detection: true,
        enable_protocol_normalize: true,
        tx_graph: true
      },
      id: "wormhole-cross-chain-grouping",
      name: "Wormhole cross-chain grouping",
      nodes: [
        {
          needs: [],
          node_name: null,
          options: {},
          step_name: "ingress",
          step_version: "0.1.0"
        },
        {
          needs: [
            "ingress"
          ],
          node_name: null,
          options: {},
          step_name: "normalize_moralis",
          step_version: "0.1.0"
        },
        {
          needs: [
            "normalize_moralis"
          ],
          node_name: null,
          options: {
            build_semantic: true,
            decode_events: true,
            decode_input: true,
            decode_internal_txs: true,
            decode_output: true,
            emit_metrics: false,
            fetch_abi_from_s3: true,
            follow_proxy: true,
            guess_abi: true,
            use_default_abis: true
          },
          step_name: "decode",
          step_version: "0.2.0"
        },
        {
          needs: [
            "decode"
          ],
          node_name: null,
          options: {},
          step_name: "protocol_detect",
          step_version: "0.1.0"
        },
        {
          needs: [
            "protocol_detect"
          ],
          node_name: null,
          options: {},
          step_name: "protocol_normalize",
          step_version: "0.1.0"
        },
        {
          needs: [
            "protocol_normalize"
          ],
          node_name: null,
          options: {},
          step_name: "metadata_extract",
          step_version: "0.1.0"
        },
        {
          needs: [
            "decode"
          ],
          node_name: null,
          options: {},
          step_name: "parse_user_ops",
          step_version: "0.1.0"
        },
        {
          needs: [
            "parse_user_ops"
          ],
          node_name: null,
          options: {},
          step_name: "parse_safe_execution",
          step_version: "0.1.0"
        },
        {
          needs: [
            "parse_safe_execution"
          ],
          node_name: null,
          options: {},
          step_name: "parse_proxy",
          step_version: "0.1.0"
        },
        {
          needs: [
            "decode"
          ],
          node_name: null,
          options: {},
          step_name: "tx_graph_build",
          step_version: "0.1.0"
        },
        {
          needs: [
            "parse_proxy",
            "tx_graph_build"
          ],
          node_name: null,
          options: {},
          step_name: "persist",
          step_version: "2.0.0"
        },
        {
          needs: [
            "parse_proxy"
          ],
          node_name: null,
          options: {},
          step_name: "group",
          step_version: "0.1.0"
        },
        {
          needs: [
            "group"
          ],
          node_name: null,
          options: {},
          step_name: "context_signal_project",
          step_version: "0.1.0"
        },
        {
          needs: [
            "context_signal_project",
            "protocol_detect"
          ],
          node_name: null,
          options: {},
          step_name: "trigger_evaluate",
          step_version: "0.1.0"
        }
      ],
      samples: [
        {
          address: null,
          chain_id: null,
          count: 3,
          description: "A multi-hop Wormhole saga spanning two Portal Token Bridge transfers / two VAAs. The BSC source transfer publishes VAA #1; the Ethereum leg is a HUB that redeems VAA #1 AND re-bridges onward, publishing VAA #2 to Base; the Base leg redeems VAA #2 (the 'Executed Tx Hash' on WormholeScan) and delivers the actual amount. Grouping converges the legs into TWO bridge.wormhole groups that share the Ethereum hub (destination of the first, source of the second), each on its own composite VAA key.",
          id: "wormhole-bsc-eth-base-multihop",
          name: "Wormhole multi-hop: BSC \u2192 Ethereum \u2192 Base",
          selector: null,
          source_kind: "tx_specs",
          transaction_types: null,
          tx_hashes: [],
          tx_specs: [
            {
              chain_id: "0x38",
              label: "BSC source Token Bridge transfer (VAA #1)",
              tx_hash: "0x068900a166004c67cd05da8d2fdd94d6afec48955baa5dcd359d3034c45c0770"
            },
            {
              chain_id: "0x1",
              label: "Ethereum hub: redeem VAA #1 + publish VAA #2",
              tx_hash: "0x56ce6bb2bfcc76a7a8592bf0196e7f8edea7298232aa6bc9060882686783e984"
            },
            {
              chain_id: "0x2105",
              label: "Base destination completeTransfer (VAA #2)",
              tx_hash: "0xe03e3763adb657b231567cd72415b67f5d0416de9a2545f58a4049517e47ea56"
            }
          ]
        }
      ],
      target_tier: "confirmed"
    },
    {
      category: "Protocol",
      config: {},
      description: "An intent's two halves, grouped. A CoW order is PLACED in one transaction and FILLED in another \u2014 same chain, minutes apart, and by different senders: the trader places, a solver fills. The two touch different contracts and move different tokens, so nothing about them looks related. What ties them is the orderUid, the protocol's own identity for the order (EIP-712 digest + owner + validTo). The settlement's Trade log states it verbatim; the ETH-flow placement does NOT, so the decoder derives it from the order the OrderPlacement log states and stamps it under the same fact \u2014 which is what lets the tx_grouping engine converge the two legs into one exchange.cowswap_order group. Deliberately NOT matched on appData: that is a hash of interface metadata (app code, slippage, version) with no order-specific content, so thousands of unrelated orders share one value \u2014 it agrees on any single pair and groups strangers at scale. Only ETH-flow and pre-signed orders have an on-chain placement; an ordinary CoW order is signed off-chain, so its fill correctly stands alone and the saga completes on the FILL role alone. Definition-driven (the cowswap ProtocolDefinition owns the order-uid linkage + the CoW Explorer deep-link).",
      dynamic_step_budget: 8,
      feature_flags: {
        enable_grouping: true,
        enable_metadata_registry: true,
        enable_protocol_detection: true,
        enable_protocol_normalize: true,
        tx_graph: true
      },
      id: "cowswap-order-lifecycle-grouping",
      name: "CoW Protocol order lifecycle grouping",
      nodes: [
        {
          needs: [],
          node_name: null,
          options: {},
          step_name: "ingress",
          step_version: "0.1.0"
        },
        {
          needs: [
            "ingress"
          ],
          node_name: null,
          options: {},
          step_name: "normalize_moralis",
          step_version: "0.1.0"
        },
        {
          needs: [
            "normalize_moralis"
          ],
          node_name: null,
          options: {
            build_semantic: true,
            decode_events: true,
            decode_input: true,
            decode_internal_txs: true,
            decode_output: true,
            emit_metrics: false,
            fetch_abi_from_s3: true,
            follow_proxy: true,
            guess_abi: true,
            use_default_abis: true
          },
          step_name: "decode",
          step_version: "0.2.0"
        },
        {
          needs: [
            "decode"
          ],
          node_name: null,
          options: {},
          step_name: "protocol_detect",
          step_version: "0.1.0"
        },
        {
          needs: [
            "protocol_detect"
          ],
          node_name: null,
          options: {},
          step_name: "protocol_normalize",
          step_version: "0.1.0"
        },
        {
          needs: [
            "protocol_normalize"
          ],
          node_name: null,
          options: {},
          step_name: "metadata_extract",
          step_version: "0.1.0"
        },
        {
          needs: [
            "decode"
          ],
          node_name: null,
          options: {},
          step_name: "parse_user_ops",
          step_version: "0.1.0"
        },
        {
          needs: [
            "parse_user_ops"
          ],
          node_name: null,
          options: {},
          step_name: "parse_safe_execution",
          step_version: "0.1.0"
        },
        {
          needs: [
            "parse_safe_execution"
          ],
          node_name: null,
          options: {},
          step_name: "parse_proxy",
          step_version: "0.1.0"
        },
        {
          needs: [
            "decode"
          ],
          node_name: null,
          options: {},
          step_name: "tx_graph_build",
          step_version: "0.1.0"
        },
        {
          needs: [
            "parse_proxy",
            "tx_graph_build"
          ],
          node_name: null,
          options: {},
          step_name: "persist",
          step_version: "2.0.0"
        },
        {
          needs: [
            "parse_proxy"
          ],
          node_name: null,
          options: {},
          step_name: "group",
          step_version: "0.1.0"
        },
        {
          needs: [
            "group"
          ],
          node_name: null,
          options: {},
          step_name: "context_signal_project",
          step_version: "0.1.0"
        },
        {
          needs: [
            "context_signal_project",
            "protocol_detect"
          ],
          node_name: null,
          options: {},
          step_name: "trigger_evaluate",
          step_version: "0.1.0"
        }
      ],
      samples: [
        {
          address: null,
          chain_id: null,
          count: 2,
          description: "A native-ETH (ETH-flow) order on Ethereum. The placement escrows 0.01 ETH through the ETH-flow wrapper and emits OrderPlacement; a solver's settlement fills it minutes later and the GPv2Settlement emits Trade. Both legs carry order uid 0xd8173b42\u2026ffffffff \u2014 verbatim on the fill, derived on the placement \u2014 so they converge into ONE completed exchange.cowswap_order group. Two traps this sample exercises: the validTo inside the signed order is the ETH-flow sentinel 0xffffffff and not the user's deadline, and the order's owner is the ETH-flow CONTRACT rather than the person who sent the transaction.",
          id: "cowswap-ethflow-place-then-fill",
          name: "CoW ETH-flow: place, then fill",
          selector: null,
          source_kind: "tx_specs",
          transaction_types: null,
          tx_hashes: [],
          tx_specs: [
            {
              chain_id: "0x1",
              label: "Ethereum PLACEMENT (ETH-flow createOrder)",
              tx_hash: "0x08615d36ac50055ab6e2aa32ccaf93dd70c903589c8ec8958faa44b92d052c3e"
            },
            {
              chain_id: "0x1",
              label: "Ethereum FILL (solver settlement, Trade log)",
              tx_hash: "0xcda47977f16dc49e8f2065cca203b1cac2fc7d1ae9dc221a39ea0b6aeb34154a"
            }
          ]
        }
      ],
      target_tier: "confirmed"
    },
    {
      category: "Protocol",
      config: {},
      description: "One prediction market's whole story, grouped on the question it asks. Six transactions spread over sixty thousand blocks \u2014 the market is created, listed for trading, positions are opened and closed on it, somebody trades shares on the order book, and finally it resolves \u2014 with different senders, different contracts and different tokens at every step. What ties them is the conditionId: the oracle, the question and the outcome count hashed together, which every position on the market shares. It is in a DIFFERENT PLACE on every leg, which is the whole reason this is a spec rather than a rule. ConditionPreparation and ConditionResolution key on the market, so it is topics[1]. PositionSplit and PositionsMerge key on the stakeholder and carry the market third. PayoutRedemption does not index it at all \u2014 it indexes the collateral token instead \u2014 so the market sits inline in the data. The order book cannot state it in any of those places: a fill names the ERC-1155 outcome share and never the question, and the derivation between them runs one way only (positionId is a keccak over the collateral and a collectionId, itself an alt_bn128 curve point over the conditionId and an index set). So the market is STAMPED on the fill, read back from the exchange's own TokenRegistered listing through the context store \u2014 which makes this template a test of the cross-transaction store as much as of the grouping engine. Feed the listing before the trade or the trade correctly joins nothing. Definition-driven: `conditional_tokens` owns the CTF's three lifecycle events, `polymarket_ctf_exchange` owns the order book's two generations, and `commons.grouping.markets` owns the spec.",
      dynamic_step_budget: 8,
      feature_flags: {
        enable_grouping: true,
        enable_metadata_registry: true,
        enable_protocol_detection: true,
        enable_protocol_normalize: true,
        tx_graph: true
      },
      id: "polymarket-market-lifecycle-grouping",
      name: "Polymarket market lifecycle grouping",
      nodes: [
        {
          needs: [],
          node_name: null,
          options: {},
          step_name: "ingress",
          step_version: "0.1.0"
        },
        {
          needs: [
            "ingress"
          ],
          node_name: null,
          options: {},
          step_name: "normalize_moralis",
          step_version: "0.1.0"
        },
        {
          needs: [
            "normalize_moralis"
          ],
          node_name: null,
          options: {
            build_semantic: true,
            decode_events: true,
            decode_input: true,
            decode_internal_txs: true,
            decode_output: true,
            emit_metrics: false,
            fetch_abi_from_s3: true,
            follow_proxy: true,
            guess_abi: true,
            use_default_abis: true
          },
          step_name: "decode",
          step_version: "0.2.0"
        },
        {
          needs: [
            "decode"
          ],
          node_name: null,
          options: {},
          step_name: "protocol_detect",
          step_version: "0.1.0"
        },
        {
          needs: [
            "protocol_detect"
          ],
          node_name: null,
          options: {},
          step_name: "protocol_normalize",
          step_version: "0.1.0"
        },
        {
          needs: [
            "protocol_normalize"
          ],
          node_name: null,
          options: {},
          step_name: "metadata_extract",
          step_version: "0.1.0"
        },
        {
          needs: [
            "decode"
          ],
          node_name: null,
          options: {},
          step_name: "parse_user_ops",
          step_version: "0.1.0"
        },
        {
          needs: [
            "parse_user_ops"
          ],
          node_name: null,
          options: {},
          step_name: "parse_safe_execution",
          step_version: "0.1.0"
        },
        {
          needs: [
            "parse_safe_execution"
          ],
          node_name: null,
          options: {},
          step_name: "parse_proxy",
          step_version: "0.1.0"
        },
        {
          needs: [
            "decode"
          ],
          node_name: null,
          options: {},
          step_name: "tx_graph_build",
          step_version: "0.1.0"
        },
        {
          needs: [
            "parse_proxy",
            "tx_graph_build"
          ],
          node_name: null,
          options: {},
          step_name: "persist",
          step_version: "2.0.0"
        },
        {
          needs: [
            "decode"
          ],
          node_name: null,
          options: {},
          step_name: "context_market_write",
          step_version: "0.1.0"
        },
        {
          needs: [
            "parse_proxy",
            "context_market_write"
          ],
          node_name: null,
          options: {},
          step_name: "group",
          step_version: "0.1.0"
        },
        {
          needs: [
            "group"
          ],
          node_name: null,
          options: {},
          step_name: "context_signal_project",
          step_version: "0.1.0"
        },
        {
          needs: [
            "context_signal_project",
            "protocol_detect"
          ],
          node_name: null,
          options: {},
          step_name: "trigger_evaluate",
          step_version: "0.1.0"
        }
      ],
      samples: [
        {
          address: null,
          chain_id: null,
          count: 6,
          description: "Market 0xf2b1250d\u2026910bd on Polygon, in chain order. Prepared at block 91,942,581 and listed for trading forty-nine blocks later; split, traded and merged around block 91,999,xxx; resolved at 92,000,172. All six legs converge into ONE open market.lifecycle group. The trade is the leg that matters. It is a PURE SECONDARY trade \u2014 both sides already held shares, so the exchange only moved them and no CTF lifecycle event fired at all. Measured over 80 real settlements, 67 already grouped because they split or merged in the same transaction; 13 looked exactly like this one and joined nothing. It groups here only because the listing three legs earlier taught the context store which market that share belongs to. The group stays OPEN after resolution on purpose: holders redeem for months afterwards, so resolution is a leg and not an ending.",
          id: "polymarket-one-market-end-to-end",
          name: "One Polygon market, created to resolved",
          selector: null,
          source_kind: "tx_specs",
          transaction_types: null,
          tx_hashes: [],
          tx_specs: [
            {
              chain_id: "0x89",
              label: "Polygon market CREATED (ConditionPreparation)",
              tx_hash: "0x56767567db978d9365a65a3d6ad0564872228927238e23e57f53ae46631870bf"
            },
            {
              chain_id: "0x89",
              label: "Polygon market LISTED (TokenRegistered \u2014 the index)",
              tx_hash: "0xc2b85d10c1b1bc749c7ea085e70f948990e7fb55792ac33d3ebcdaf05da3a064"
            },
            {
              chain_id: "0x89",
              label: "Polygon position OPENED (PositionSplit)",
              tx_hash: "0x87bc3abe8435e7552b7bf81aa3259892145e2a7f90ad15a9bfa2a4ec788bfd2a"
            },
            {
              chain_id: "0x89",
              label: "Polygon SECONDARY TRADE (OrderFilled \u2014 stamped)",
              tx_hash: "0x14823b37c047e8467e6bef00f30567c7e585f282043a8391431aaba90e2a1af2"
            },
            {
              chain_id: "0x89",
              label: "Polygon position CLOSED (PositionsMerge)",
              tx_hash: "0x3411d6dc613dfa66afb6d0263b503ede5f74a549678ef662b2d032d7e07451aa"
            },
            {
              chain_id: "0x89",
              label: "Polygon market RESOLVED (ConditionResolution)",
              tx_hash: "0x869cdf4a193b6f11caecbf0af81de94371c17cdda9fe7f86860d7a339a89ac49"
            }
          ]
        }
      ],
      target_tier: "confirmed"
    },
    {
      category: "Protocol",
      config: {},
      description: "Cross-chain Mayan grouping across the Mayan Forwarder's sub-protocols (Swift / Swift v2 / MCTP / mono-chain swap). The Forwarder entrypoint is unverified-rotation-resistant (claimed by selector, ABI resolved at runtime) and most real transactions are aggregator-wrapped (MetaMask MetaBridge, ERC-4337, LiFi) so the Mayan call lands in an INTERNAL transaction \u2014 the decode step walks internal txs, resolves the verified Mayan/DEX ABIs, and peels the opaque mayanData blob from the definition-owned layouts (version + block aware). For a Mayan Swift swap the source SEND, destination FULFILL, and source UNLOCK legs all carry the SAME Mayan order hash, so the tx_grouping engine converges them into one bridge.mayan group with a confidence score + evidence (even ingested out of order). MCTP (Circle CCTP + Wormhole) and a same-chain mono-chain swap decode as standalone intents. Persists the decoded transactions to the application tables and the TransactionGroup state + tx\u2194group overlays to the grouping tables. Definition-driven (the mayan ProtocolDefinition owns the order-hash cross-chain linkage + the Mayan Explorer deep-link).",
      dynamic_step_budget: 8,
      feature_flags: {
        enable_grouping: true,
        enable_metadata_registry: true,
        enable_protocol_detection: true,
        enable_protocol_normalize: true,
        tx_graph: true
      },
      id: "mayan-cross-chain-grouping",
      name: "Mayan cross-chain grouping",
      nodes: [
        {
          needs: [],
          node_name: null,
          options: {},
          step_name: "ingress",
          step_version: "0.1.0"
        },
        {
          needs: [
            "ingress"
          ],
          node_name: null,
          options: {},
          step_name: "normalize_moralis",
          step_version: "0.1.0"
        },
        {
          needs: [
            "normalize_moralis"
          ],
          node_name: null,
          options: {
            build_semantic: true,
            decode_events: true,
            decode_input: true,
            decode_internal_txs: true,
            decode_output: true,
            emit_metrics: false,
            fetch_abi_from_s3: true,
            follow_proxy: true,
            guess_abi: true,
            use_default_abis: true
          },
          step_name: "decode",
          step_version: "0.2.0"
        },
        {
          needs: [
            "decode"
          ],
          node_name: null,
          options: {},
          step_name: "protocol_detect",
          step_version: "0.1.0"
        },
        {
          needs: [
            "protocol_detect"
          ],
          node_name: null,
          options: {},
          step_name: "protocol_normalize",
          step_version: "0.1.0"
        },
        {
          needs: [
            "protocol_normalize"
          ],
          node_name: null,
          options: {},
          step_name: "metadata_extract",
          step_version: "0.1.0"
        },
        {
          needs: [
            "decode"
          ],
          node_name: null,
          options: {},
          step_name: "parse_user_ops",
          step_version: "0.1.0"
        },
        {
          needs: [
            "parse_user_ops"
          ],
          node_name: null,
          options: {},
          step_name: "parse_safe_execution",
          step_version: "0.1.0"
        },
        {
          needs: [
            "parse_safe_execution"
          ],
          node_name: null,
          options: {},
          step_name: "parse_proxy",
          step_version: "0.1.0"
        },
        {
          needs: [
            "decode"
          ],
          node_name: null,
          options: {},
          step_name: "tx_graph_build",
          step_version: "0.1.0"
        },
        {
          needs: [
            "parse_proxy",
            "tx_graph_build"
          ],
          node_name: null,
          options: {},
          step_name: "persist",
          step_version: "2.0.0"
        },
        {
          needs: [
            "parse_proxy"
          ],
          node_name: null,
          options: {},
          step_name: "group",
          step_version: "0.1.0"
        },
        {
          needs: [
            "group"
          ],
          node_name: null,
          options: {},
          step_name: "context_signal_project",
          step_version: "0.1.0"
        },
        {
          needs: [
            "context_signal_project",
            "protocol_detect"
          ],
          node_name: null,
          options: {},
          step_name: "trigger_evaluate",
          step_version: "0.1.0"
        }
      ],
      samples: [
        {
          address: null,
          chain_id: null,
          count: 3,
          description: "A Mayan Swift v2 order (BSC \u2192 Ethereum). The source SEND locks funds on BSC (wrapped via MetaMask MetaBridge \u2192 LiFi \u2192 the Mayan Forwarder), the destination FULFILL delivers them to the user on Ethereum, and the source UNLOCK releases the locked funds to the solver on BSC after proving fulfillment. All three legs carry the same Mayan order hash, so grouping converges them into one bridge.mayan group (SEND + FULFILL as source/destination, UNLOCK as the source-side settlement).",
          id: "mayan-swift-v2-bsc-eth",
          name: "Mayan Swift v2: BSC \u2192 Ethereum",
          selector: null,
          source_kind: "tx_specs",
          transaction_types: null,
          tx_hashes: [],
          tx_specs: [
            {
              chain_id: "0x38",
              label: "BSC source SEND (Swift order created)",
              tx_hash: "0xd0a6be5d23f6c9752d0ff842fb953f4456a1c0d2895be71ffd56265f127b8739"
            },
            {
              chain_id: "0x1",
              label: "Ethereum destination FULFILL",
              tx_hash: "0x7d0b5362274891f90a691aa09a69124fce0ab04169cea12822c0663eace5277d"
            },
            {
              chain_id: "0x38",
              label: "BSC source UNLOCK (solver settles)",
              tx_hash: "0x4c9113ad5ff2f0ba3f6bf165d2e5a2154a4ade8ce4fbd9324c8e45c174c5a277"
            }
          ]
        },
        {
          address: null,
          chain_id: null,
          count: 2,
          description: "A Mayan Swift order between Ethereum and Solana \u2014 only the EVM legs are decoded here (the Solana SETTLE / FULFILL / REGISTER_ORDER legs are out of EVM decode scope). The Ethereum SEND creates the order and the Ethereum UNLOCK settles it; both carry the same Mayan order hash and group into one bridge.mayan saga whose counterpart leg lives on Solana.",
          id: "mayan-swift-eth-solana-evm-legs",
          name: "Mayan Swift: Ethereum \u2194 Solana (EVM legs)",
          selector: null,
          source_kind: "tx_specs",
          transaction_types: null,
          tx_hashes: [],
          tx_specs: [
            {
              chain_id: "0x1",
              label: "Ethereum SEND (Swift order created)",
              tx_hash: "0x2013b31a0ac6f73fd492dbd986f000253aa284fabe752ba3b6a03d485e6182fe"
            },
            {
              chain_id: "0x1",
              label: "Ethereum UNLOCK (order settled)",
              tx_hash: "0x568289c909bb7193843faf1d52ade55ba05625c2c37f26294ddec8156b5d3eb8"
            }
          ]
        },
        {
          address: null,
          chain_id: null,
          count: 2,
          description: "A Mayan MCTP (Mayan Circle Transfer Protocol) USDC transfer Ethereum \u2192 Base. The source SEND (aggregator-wrapped ERC-4337 \u2192 LiFi \u2192 the Mayan Forwarder \u2192 MCTP bridgeWithFee) burns USDC through Circle CCTP (depositForBurnWithCaller) and publishes the order as a Wormhole message (LogMessagePublished); the destination SETTLE (redeemWithFee) verifies that VAA, mints USDC via CCTP, pays the relayer fee, and delivers the remainder to the recipient. Both legs reference the SAME Wormhole VAA (emitterAddress + sequence), so grouping converges them into one bridge.mayan group (SEND source / SETTLE destination) with a Mayan Explorer deep-link.",
          id: "mayan-mctp-eth-base",
          name: "Mayan MCTP: Ethereum \u2192 Base",
          selector: null,
          source_kind: "tx_specs",
          transaction_types: null,
          tx_hashes: [],
          tx_specs: [
            {
              chain_id: "0x1",
              label: "Ethereum source SEND (MCTP bridgeWithFee)",
              tx_hash: "0x76fb83df14db62c00b6ed457626987a3cb7172f6e55cc1a88430bc63a01f0571"
            },
            {
              chain_id: "0x2105",
              label: "Base destination SETTLE (MCTP redeemWithFee)",
              tx_hash: "0xbbe36d09019c40a1acf774a1244f6d160f28d06f9efba2bd9364eda9c9483f97"
            }
          ]
        },
        {
          address: null,
          chain_id: null,
          count: 1,
          description: "A Mayan same-chain (mono-chain) swap on Ethereum through the Forwarder + a Uniswap V4 route. No cross-chain group \u2014 it decodes as a single swap intent with a Mayan Explorer deep-link.",
          id: "mayan-mono-chain-eth",
          name: "Mayan mono-chain swap (Ethereum)",
          selector: null,
          source_kind: "tx_specs",
          transaction_types: null,
          tx_hashes: [],
          tx_specs: [
            {
              chain_id: "0x1",
              label: "Ethereum mono-chain swap",
              tx_hash: "0xc283e5a7f3fccf89c79e9b0c0f0bb145493c431348ba11eecc600895552518d8"
            }
          ]
        }
      ],
      target_tier: "confirmed"
    },
    {
      category: "Tokens",
      config: {},
      description: "Resolve token-definition metadata (symbol / decimals / name / logo) next to every token address a transaction touches. ingress \u2192 normalize (Moralis) \u2192 decode (calldata + ERC-20/721 events + proxy resolution, build_semantic on) \u2192 metadata_extract \u2192 tx_graph_build \u2192 persist. The decode step hands each token address to the token SSOT (commons.tokens: bundled registry \u2192 our own DynamoDB cache \u2192 third-party Moralis, with negative caching + self-hosted logos), so each SemanticAsset carries its symbol + decimals beside its token_address and the GraphScene assets carry their logo. Run it through the local pipeline-run harness with the token_definitions definition (decode mode: abi) to resolve unknown tokens live from the configured third parties.",
      dynamic_step_budget: 8,
      feature_flags: {
        enable_grouping: true,
        enable_metadata_registry: true,
        enable_protocol_detection: true,
        enable_protocol_normalize: true,
        tx_graph: true
      },
      id: "token-definitions",
      name: "Token definitions enrichment",
      nodes: [
        {
          needs: [],
          node_name: null,
          options: {},
          step_name: "ingress",
          step_version: "0.1.0"
        },
        {
          needs: [
            "ingress"
          ],
          node_name: null,
          options: {},
          step_name: "normalize_moralis",
          step_version: "0.1.0"
        },
        {
          needs: [
            "normalize_moralis"
          ],
          node_name: null,
          options: {
            build_semantic: true,
            decode_events: true,
            decode_input: true,
            decode_internal_txs: true,
            decode_output: true,
            fetch_abi_from_s3: true,
            follow_proxy: true,
            guess_abi: true,
            use_default_abis: true
          },
          step_name: "decode",
          step_version: "0.2.0"
        },
        {
          needs: [
            "decode"
          ],
          node_name: null,
          options: {},
          step_name: "enrich_asset_metadata",
          step_version: "0.1.0"
        },
        {
          needs: [
            "decode"
          ],
          node_name: null,
          options: {},
          step_name: "protocol_detect",
          step_version: "0.1.0"
        },
        {
          needs: [
            "protocol_detect"
          ],
          node_name: null,
          options: {},
          step_name: "protocol_normalize",
          step_version: "0.1.0"
        },
        {
          needs: [
            "protocol_normalize"
          ],
          node_name: null,
          options: {},
          step_name: "metadata_extract",
          step_version: "0.1.0"
        },
        {
          needs: [
            "decode"
          ],
          node_name: null,
          options: {},
          step_name: "tx_graph_build",
          step_version: "0.1.0"
        },
        {
          needs: [
            "tx_graph_build"
          ],
          node_name: null,
          options: {},
          step_name: "persist",
          step_version: "2.0.0"
        },
        {
          needs: [
            "decode"
          ],
          node_name: null,
          options: {},
          step_name: "group",
          step_version: "0.1.0"
        },
        {
          needs: [
            "group"
          ],
          node_name: null,
          options: {},
          step_name: "context_signal_project",
          step_version: "0.1.0"
        },
        {
          needs: [
            "context_signal_project",
            "protocol_detect"
          ],
          node_name: null,
          options: {},
          step_name: "trigger_evaluate",
          step_version: "0.1.0"
        }
      ],
      samples: [
        {
          address: null,
          chain_id: null,
          count: 2,
          description: "Two real EVM transactions that move ERC-20 tokens \u2014 one on Ethereum, one on Base. Each is decoded and every token address is resolved to its symbol / decimals / name / logo via the token SSOT (bundled registry \u2192 our DynamoDB cache \u2192 third-party Moralis), so the metadata renders next to the addresses. Mirrors the token_definitions pipeline-run harness fixtures.",
          id: "token-defs-evm-multichain",
          name: "Token metadata: Ethereum + Base",
          selector: null,
          source_kind: "tx_specs",
          transaction_types: null,
          tx_hashes: [],
          tx_specs: [
            {
              chain_id: "0x1",
              label: "Ethereum tx \u2014 resolve token defs next to transfer addresses",
              tx_hash: "0x0319d6769960690d637e0bac234272395e149ce35422169b85c3534f41a099bf"
            },
            {
              chain_id: "0x2105",
              label: "Base tx \u2014 resolve token defs next to transfer addresses",
              tx_hash: "0x3858e79e42b937cc65355a6988acf80e3cbf0c6a13afe30b72f406e5f8cdcef3"
            }
          ]
        }
      ],
      target_tier: "confirmed"
    },
    {
      category: "Protocol",
      config: {},
      description: "Protocol pipeline for the whole Uniswap family on Ethereum: ingress \u2192 normalize (Moralis) \u2192 decode \u2192 parse user-ops / Safe / proxy \u2192 build graph \u2192 persist. Decodes V1 factory/exchange swaps + liquidity, V2/V3 router swaps, V4 PositionManager NFT liquidity, and the Universal Router command dispatch (with Permit2 pulls + wrap/unwrap), peeling the nested calldata each packs behind routers and aggregators. build_semantic on so the swap/liquidity tree lands in the render-ready GraphScene. Ships one runnable sample per version family (V1 / V2 / V3 / V4 position / Permit2).",
      dynamic_step_budget: 8,
      feature_flags: {
        enable_grouping: true,
        enable_instance_lifecycle: true,
        enable_metadata_registry: true,
        enable_protocol_detection: true,
        enable_protocol_normalize: true,
        tx_graph: true
      },
      id: "uniswap-family",
      name: "Uniswap family (V1-V4 + Permit2)",
      nodes: [
        {
          needs: [],
          node_name: null,
          options: {},
          step_name: "ingress",
          step_version: "0.1.0"
        },
        {
          needs: [
            "ingress"
          ],
          node_name: null,
          options: {},
          step_name: "normalize_moralis",
          step_version: "0.1.0"
        },
        {
          needs: [
            "normalize_moralis"
          ],
          node_name: null,
          options: {
            build_semantic: true,
            decode_events: true,
            decode_input: true,
            decode_internal_txs: true,
            decode_output: true,
            fetch_abi_from_s3: true,
            follow_proxy: true,
            guess_abi: true,
            use_default_abis: true
          },
          step_name: "decode",
          step_version: "0.2.0"
        },
        {
          needs: [
            "decode"
          ],
          node_name: null,
          options: {},
          step_name: "protocol_detect",
          step_version: "0.1.0"
        },
        {
          needs: [
            "protocol_detect"
          ],
          node_name: null,
          options: {},
          step_name: "protocol_normalize",
          step_version: "0.1.0"
        },
        {
          needs: [
            "protocol_detect"
          ],
          node_name: null,
          options: {},
          step_name: "price_enrich",
          step_version: "0.1.0"
        },
        {
          needs: [
            "protocol_normalize",
            "price_enrich",
            "instance_lifecycle"
          ],
          node_name: null,
          options: {},
          step_name: "metadata_extract",
          step_version: "0.1.0"
        },
        {
          needs: [
            "protocol_normalize"
          ],
          node_name: null,
          options: {},
          step_name: "instance_lifecycle",
          step_version: "0.1.0"
        },
        {
          needs: [
            "decode"
          ],
          node_name: null,
          options: {},
          step_name: "parse_user_ops",
          step_version: "0.1.0"
        },
        {
          needs: [
            "parse_user_ops"
          ],
          node_name: null,
          options: {},
          step_name: "parse_safe_execution",
          step_version: "0.1.0"
        },
        {
          needs: [
            "parse_safe_execution"
          ],
          node_name: null,
          options: {},
          step_name: "parse_proxy",
          step_version: "0.1.0"
        },
        {
          needs: [
            "decode"
          ],
          node_name: null,
          options: {},
          step_name: "tx_graph_build",
          step_version: "0.1.0"
        },
        {
          needs: [
            "parse_proxy",
            "tx_graph_build",
            "price_enrich"
          ],
          node_name: null,
          options: {},
          step_name: "persist",
          step_version: "2.0.0"
        },
        {
          needs: [
            "parse_proxy"
          ],
          node_name: null,
          options: {},
          step_name: "group",
          step_version: "0.1.0"
        },
        {
          needs: [
            "instance_lifecycle",
            "group"
          ],
          node_name: null,
          options: {},
          step_name: "context_signal_project",
          step_version: "0.1.0"
        },
        {
          needs: [
            "context_signal_project",
            "protocol_detect",
            "price_enrich"
          ],
          node_name: null,
          options: {},
          step_name: "trigger_evaluate",
          step_version: "0.1.0"
        }
      ],
      samples: [
        {
          address: null,
          chain_id: "0x1",
          count: 2,
          description: "Two Uniswap V3 swaps on Ethereum \u2014 exact-in / exact-out single- and multi-hop routes through the SwapRouter (exactInput* path encoding + pool Swap events). Decodes the V3 path segments, fee tiers, and in/out amounts.",
          id: "uniswap-v3-swaps",
          name: "Uniswap V3 swaps (Ethereum)",
          selector: null,
          source_kind: "tx_hashes",
          transaction_types: null,
          tx_hashes: [
            "0x87de1f38787d10058e82efdcb4851bd769bc48e1eeb40c4b7f63bfbd72e47fc8",
            "0xfe5efc11fa1576a5737f51155789c0f8a62c6f20b263df42fbe3bcef0b359e52"
          ],
          tx_specs: []
        },
        {
          address: null,
          chain_id: "0x1",
          count: 4,
          description: "Four Uniswap V4 PositionManager transactions on Ethereum \u2014 mint / modify / collect / burn of the ERC-721 liquidity position (the modifyLiquidities command batch + PoolManager settlement events). Exercises the V4 position + currency pair decode.",
          id: "uniswap-v4-position",
          name: "Uniswap V4 Position NFT (Ethereum)",
          selector: null,
          source_kind: "tx_hashes",
          transaction_types: null,
          tx_hashes: [
            "0x9be1ba8a2ba8963bed8f9fce1e321848424fa2503ede055836ed9730d49b4721",
            "0xd7ed13b72c7a1cc5782d1041a256c49b787a92d986c472dfaf4894fe38880558",
            "0x112984a0abf5f60574580a0d8f13768afc8827fc50d91fb884803fd92d760e80",
            "0x86f5f6659e48c30388bc71b701682013be690dd051cad57edeed153c0d24428d"
          ],
          tx_specs: []
        },
        {
          address: null,
          chain_id: "0x1",
          count: 3,
          description: "Three Uniswap V1 transactions on Ethereum \u2014 the original 2018 Vyper AMM's per-token exchange swap / liquidity verbs (ethToTokenSwapInput / tokenToEthSwapInput / tokenToTokenSwapInput), claimed by the V1 exchange plugin's distinctive selectors.",
          id: "uniswap-v1-swaps",
          name: "Uniswap V1 swaps (Ethereum)",
          selector: null,
          source_kind: "tx_hashes",
          transaction_types: null,
          tx_hashes: [
            "0x1dd2f4fa5e77159d20e2f7b8882f21b9f24b3fee71336e1249788885cf319f61",
            "0xe5e35ee13bb6326df4da89f17504a81923299d4986de06a019ca7856cbe76bca",
            "0x4824def63ba420570e59acf10f32bd935d7a0a0d67e6eb07e4c0ae4e270b71d1"
          ],
          tx_specs: []
        },
        {
          address: null,
          chain_id: "0x1",
          count: 1,
          description: "A Uniswap V2 router swap on Ethereum (swapExactTokensForTokens family) \u2014 the constant-product pair path + Swap events.",
          id: "uniswap-v2-swap",
          name: "Uniswap V2 swap (Ethereum)",
          selector: null,
          source_kind: "tx_hashes",
          transaction_types: null,
          tx_hashes: [
            "0x006a041ade99b4715e6578ac968932f47fa9f1be1a676ad66678260ca0fbb6d1"
          ],
          tx_specs: []
        },
        {
          address: null,
          chain_id: "0x1",
          count: 1,
          description: "A Permit2 transaction on Ethereum \u2014 the approval rung of the Uniswap stack (permit / approve / transferFrom), decoded via the Permit2 emitter so the spender / token / amount surface.",
          id: "uniswap-permit2",
          name: "Permit2 approval (Ethereum)",
          selector: null,
          source_kind: "tx_hashes",
          transaction_types: null,
          tx_hashes: [
            "0x36ccf5bfee79c946dd4ba4e0cc59d2489acf703988fd709311323cf124a23703"
          ],
          tx_specs: []
        }
      ],
      target_tier: "confirmed"
    },
    {
      category: "MEV",
      config: {},
      description: "Protocol pipeline for the Banana Gun Router V2 on Ethereum: ingress \u2192 normalize (Moralis) \u2192 decode \u2192 parse user-ops / Safe / proxy \u2192 build graph \u2192 persist. Decodes the bot's buy (ETH \u2192 token) / sell (token \u2192 ETH/WETH) entrypoints \u2014 anchoring side, downstream DEX, token pair, amounts + platform fee as highlights \u2014 and forwards the actual swap to the downstream DEX (Uniswap V2/V3 + forks) via internal txs + Transfer/Swap events. build_semantic on so the SWAP tree lands in the render-ready GraphScene. Ships a 'latest N router txs' sample and a hardcoded buy/sell set.",
      dynamic_step_budget: 8,
      feature_flags: {
        enable_grouping: true,
        enable_metadata_registry: true,
        enable_protocol_detection: true,
        enable_protocol_normalize: true,
        tx_graph: true
      },
      id: "banana-gun-router",
      name: "Banana Gun router (MEV trading bot)",
      nodes: [
        {
          needs: [],
          node_name: null,
          options: {},
          step_name: "ingress",
          step_version: "0.1.0"
        },
        {
          needs: [
            "ingress"
          ],
          node_name: null,
          options: {},
          step_name: "normalize_moralis",
          step_version: "0.1.0"
        },
        {
          needs: [
            "normalize_moralis"
          ],
          node_name: null,
          options: {
            build_semantic: true,
            decode_events: true,
            decode_input: true,
            decode_internal_txs: true,
            decode_output: true,
            fetch_abi_from_s3: true,
            follow_proxy: true,
            guess_abi: true,
            use_default_abis: true
          },
          step_name: "decode",
          step_version: "0.2.0"
        },
        {
          needs: [
            "decode"
          ],
          node_name: null,
          options: {},
          step_name: "protocol_detect",
          step_version: "0.1.0"
        },
        {
          needs: [
            "protocol_detect"
          ],
          node_name: null,
          options: {},
          step_name: "protocol_normalize",
          step_version: "0.1.0"
        },
        {
          needs: [
            "protocol_normalize"
          ],
          node_name: null,
          options: {},
          step_name: "metadata_extract",
          step_version: "0.1.0"
        },
        {
          needs: [
            "decode"
          ],
          node_name: null,
          options: {},
          step_name: "parse_user_ops",
          step_version: "0.1.0"
        },
        {
          needs: [
            "parse_user_ops"
          ],
          node_name: null,
          options: {},
          step_name: "parse_safe_execution",
          step_version: "0.1.0"
        },
        {
          needs: [
            "parse_safe_execution"
          ],
          node_name: null,
          options: {},
          step_name: "parse_proxy",
          step_version: "0.1.0"
        },
        {
          needs: [
            "decode"
          ],
          node_name: null,
          options: {},
          step_name: "tx_graph_build",
          step_version: "0.1.0"
        },
        {
          needs: [
            "parse_proxy",
            "tx_graph_build"
          ],
          node_name: null,
          options: {},
          step_name: "persist",
          step_version: "2.0.0"
        },
        {
          needs: [
            "parse_proxy"
          ],
          node_name: null,
          options: {},
          step_name: "group",
          step_version: "0.1.0"
        },
        {
          needs: [
            "group"
          ],
          node_name: null,
          options: {},
          step_name: "context_signal_project",
          step_version: "0.1.0"
        },
        {
          needs: [
            "context_signal_project",
            "protocol_detect"
          ],
          node_name: null,
          options: {},
          step_name: "trigger_evaluate",
          step_version: "0.1.0"
        }
      ],
      samples: [
        {
          address: "0x3328f7f4a1d1c57c35df56bbf0c9dcafca309c49",
          chain_id: "0x1",
          count: 5,
          description: "The latest 5 transactions hitting the Banana Gun Router V2 on Ethereum \u2014 discovered live via the explorer provider (no selector filter, so both buys and sells are pulled). The 'pick the latest N' path: each is fetched verbose (full logs + internal txs) and run through the MEV decode.",
          id: "banana-gun-latest",
          name: "Banana Gun latest (last 5 router txs)",
          selector: null,
          source_kind: "latest",
          transaction_types: null,
          tx_hashes: [],
          tx_specs: []
        },
        {
          address: null,
          chain_id: "0x1",
          count: 3,
          description: "Three verified Banana Gun Router V2 transactions on Ethereum \u2014 two buys (ETH \u2192 token via Uniswap V2) and one sell (token \u2192 WETH via Uniswap V2, fully anchored: token pair / pool / amountIn / amountOutMin / recipient). The reproducible, no-network set.",
          id: "banana-gun-buys-sells",
          name: "Banana Gun buys + sell (Ethereum)",
          selector: null,
          source_kind: "tx_hashes",
          transaction_types: null,
          tx_hashes: [
            "0x9933e82d1b4f4fc5b4bcaec0c60c79b0a1a57745799546b13a2fc3260421ac7a",
            "0xf1d7181e3521a1e46e96626a21253b328cbde720a03443dec82e37669bff29d1",
            "0x220541c9a57d4b58391b0c46647e8d2ffe8667bfae30b2f24e989a4396c0c354"
          ],
          tx_specs: []
        }
      ],
      target_tier: "confirmed"
    },
    {
      category: "MEV",
      config: {},
      description: "Protocol pipeline for the generic MEV bot (0x98c3\u20266b8e) on Ethereum: ingress \u2192 normalize (Moralis) \u2192 decode \u2192 parse user-ops / Safe / proxy \u2192 build graph \u2192 persist. A closed-source arbitrage/sandwich searcher that calls a Uniswap V2 pair / V3 pool DIRECTLY with hand-packed calldata \u2014 the decoder pins the venue from the selector and anchors amountIn / amountOutMin / pair / tokenOut (V2) or pool / amountSpecified (V3), marks it IS_ARB for the single-tx net-profit headline, and delegates the swap math to the pool's own decode + the Transfer flow. Ships a 'latest N' sample and a hardcoded V2+V3 set.",
      dynamic_step_budget: 8,
      feature_flags: {
        enable_grouping: true,
        enable_metadata_registry: true,
        enable_protocol_detection: true,
        enable_protocol_normalize: true,
        tx_graph: true
      },
      id: "mev-bot-generic",
      name: "Generic MEV bot (direct-pool searcher)",
      nodes: [
        {
          needs: [],
          node_name: null,
          options: {},
          step_name: "ingress",
          step_version: "0.1.0"
        },
        {
          needs: [
            "ingress"
          ],
          node_name: null,
          options: {},
          step_name: "normalize_moralis",
          step_version: "0.1.0"
        },
        {
          needs: [
            "normalize_moralis"
          ],
          node_name: null,
          options: {
            build_semantic: true,
            decode_events: true,
            decode_input: true,
            decode_internal_txs: true,
            decode_output: true,
            fetch_abi_from_s3: true,
            follow_proxy: true,
            guess_abi: true,
            use_default_abis: true
          },
          step_name: "decode",
          step_version: "0.2.0"
        },
        {
          needs: [
            "decode"
          ],
          node_name: null,
          options: {},
          step_name: "protocol_detect",
          step_version: "0.1.0"
        },
        {
          needs: [
            "protocol_detect"
          ],
          node_name: null,
          options: {},
          step_name: "protocol_normalize",
          step_version: "0.1.0"
        },
        {
          needs: [
            "protocol_normalize"
          ],
          node_name: null,
          options: {},
          step_name: "metadata_extract",
          step_version: "0.1.0"
        },
        {
          needs: [
            "decode"
          ],
          node_name: null,
          options: {},
          step_name: "parse_user_ops",
          step_version: "0.1.0"
        },
        {
          needs: [
            "parse_user_ops"
          ],
          node_name: null,
          options: {},
          step_name: "parse_safe_execution",
          step_version: "0.1.0"
        },
        {
          needs: [
            "parse_safe_execution"
          ],
          node_name: null,
          options: {},
          step_name: "parse_proxy",
          step_version: "0.1.0"
        },
        {
          needs: [
            "decode"
          ],
          node_name: null,
          options: {},
          step_name: "tx_graph_build",
          step_version: "0.1.0"
        },
        {
          needs: [
            "parse_proxy",
            "tx_graph_build"
          ],
          node_name: null,
          options: {},
          step_name: "persist",
          step_version: "2.0.0"
        },
        {
          needs: [
            "parse_proxy"
          ],
          node_name: null,
          options: {},
          step_name: "group",
          step_version: "0.1.0"
        },
        {
          needs: [
            "group"
          ],
          node_name: null,
          options: {},
          step_name: "context_signal_project",
          step_version: "0.1.0"
        },
        {
          needs: [
            "context_signal_project",
            "protocol_detect"
          ],
          node_name: null,
          options: {},
          step_name: "trigger_evaluate",
          step_version: "0.1.0"
        }
      ],
      samples: [
        {
          address: "0x98c3d3183c4b8a650614ad179a1a98be0a8d6b8e",
          chain_id: "0x1",
          count: 5,
          description: "The latest 5 transactions hitting the generic MEV bot (0x98c3\u20266b8e) on Ethereum \u2014 discovered live via the explorer provider (no selector filter). Each is fetched verbose (full logs + internal txs) and run through the MEV decode.",
          id: "mev-bot-latest",
          name: "Generic MEV bot latest (last 5 txs)",
          selector: null,
          source_kind: "latest",
          transaction_types: null,
          tx_hashes: [],
          tx_specs: []
        },
        {
          address: null,
          chain_id: "0x1",
          count: 2,
          description: "Two verified generic-MEV-bot txs on Ethereum: a direct Uniswap V2 pair swap (0xce2e62ff \u2014 anchors amountIn / amountOutMin / pair / tokenOut) and a direct Uniswap V3 pool swap (0x49c36c07 \u2014 anchors pool / amountSpecified / zeroForOne). The reproducible, no-network set.",
          id: "mev-bot-v2-v3",
          name: "Generic MEV bot V2 + V3 (Ethereum)",
          selector: null,
          source_kind: "tx_hashes",
          transaction_types: null,
          tx_hashes: [
            "0xebec2abc8affbaa27bc76839237e1d1de151d51e7ab340614f422b9f4cff4083",
            "0xfc1359a6cc9369c78d740e79fc1b60fe0ccbaa1d04963a677b6d8c5905bcc428"
          ],
          tx_specs: []
        }
      ],
      target_tier: "confirmed"
    },
    {
      category: "MEV",
      config: {},
      description: "Protocol pipeline for jaredfromsubway.eth (0x1f2f\u2026f387) on Ethereum: ingress \u2192 normalize (Moralis) \u2192 decode \u2192 parse user-ops / Safe / proxy \u2192 build graph \u2192 persist. A heavily obfuscated sandwich/arb searcher that shares the generic bot's direct-pool dialect (reused layouts, IS_ARB net-profit headline). Its live entrypoints mostly use selectors the plugin does not yet model, so those decode through the generic seams (ERC-20 Transfer flows show the sandwich legs); the plugin claims jared only on the shared direct-pool selectors. Ships a 'latest N' sample and a hardcoded set of recent txs.",
      dynamic_step_budget: 8,
      feature_flags: {
        enable_grouping: true,
        enable_metadata_registry: true,
        enable_protocol_detection: true,
        enable_protocol_normalize: true,
        tx_graph: true
      },
      id: "jaredfromsubway",
      name: "jaredfromsubway.eth (MEV sandwich bot)",
      nodes: [
        {
          needs: [],
          node_name: null,
          options: {},
          step_name: "ingress",
          step_version: "0.1.0"
        },
        {
          needs: [
            "ingress"
          ],
          node_name: null,
          options: {},
          step_name: "normalize_moralis",
          step_version: "0.1.0"
        },
        {
          needs: [
            "normalize_moralis"
          ],
          node_name: null,
          options: {
            build_semantic: true,
            decode_events: true,
            decode_input: true,
            decode_internal_txs: true,
            decode_output: true,
            fetch_abi_from_s3: true,
            follow_proxy: true,
            guess_abi: true,
            use_default_abis: true
          },
          step_name: "decode",
          step_version: "0.2.0"
        },
        {
          needs: [
            "decode"
          ],
          node_name: null,
          options: {},
          step_name: "protocol_detect",
          step_version: "0.1.0"
        },
        {
          needs: [
            "protocol_detect"
          ],
          node_name: null,
          options: {},
          step_name: "protocol_normalize",
          step_version: "0.1.0"
        },
        {
          needs: [
            "protocol_normalize"
          ],
          node_name: null,
          options: {},
          step_name: "metadata_extract",
          step_version: "0.1.0"
        },
        {
          needs: [
            "decode"
          ],
          node_name: null,
          options: {},
          step_name: "parse_user_ops",
          step_version: "0.1.0"
        },
        {
          needs: [
            "parse_user_ops"
          ],
          node_name: null,
          options: {},
          step_name: "parse_safe_execution",
          step_version: "0.1.0"
        },
        {
          needs: [
            "parse_safe_execution"
          ],
          node_name: null,
          options: {},
          step_name: "parse_proxy",
          step_version: "0.1.0"
        },
        {
          needs: [
            "decode"
          ],
          node_name: null,
          options: {},
          step_name: "tx_graph_build",
          step_version: "0.1.0"
        },
        {
          needs: [
            "parse_proxy",
            "tx_graph_build"
          ],
          node_name: null,
          options: {},
          step_name: "persist",
          step_version: "2.0.0"
        },
        {
          needs: [
            "parse_proxy"
          ],
          node_name: null,
          options: {},
          step_name: "group",
          step_version: "0.1.0"
        },
        {
          needs: [
            "group"
          ],
          node_name: null,
          options: {},
          step_name: "context_signal_project",
          step_version: "0.1.0"
        },
        {
          needs: [
            "context_signal_project",
            "protocol_detect"
          ],
          node_name: null,
          options: {},
          step_name: "trigger_evaluate",
          step_version: "0.1.0"
        }
      ],
      samples: [
        {
          address: "0x1f2f10d1c40777ae1da742455c65828ff36df387",
          chain_id: "0x1",
          count: 5,
          description: "The latest 5 transactions hitting jaredfromsubway.eth (0x1f2f\u2026f387) on Ethereum \u2014 discovered live via the explorer provider. Each is fetched verbose (full logs + internal txs); the sandwich legs surface as ERC-20 Transfer flows.",
          id: "jaredfromsubway-latest",
          name: "jaredfromsubway latest (last 5 txs)",
          selector: null,
          source_kind: "latest",
          transaction_types: null,
          tx_hashes: [],
          tx_specs: []
        },
        {
          address: null,
          chain_id: "0x1",
          count: 2,
          description: "Two verified recent jaredfromsubway.eth txs on Ethereum. Their obfuscated entrypoints currently decode through the generic seams \u2014 the ERC-20 Transfer flows expose the multi-hop sandwich (WETH \u2194 token movements) \u2014 rather than the plugin's anchored swap headline. The reproducible, no-network set.",
          id: "jaredfromsubway-recent",
          name: "jaredfromsubway recent txs (Ethereum)",
          selector: null,
          source_kind: "tx_hashes",
          transaction_types: null,
          tx_hashes: [
            "0xfc71603c2539bd4293c669ade4ce61f5ffa93750c4f744e2f4e9767640f8cacc",
            "0x3041400be313a5c7a268376875ed08af10162f775cf22b293e05c2c3a9996a2d"
          ],
          tx_specs: []
        }
      ],
      target_tier: "confirmed"
    },
    {
      category: "MEV",
      config: {},
      description: "Protocol pipeline for the Wintermute market-maker router (0x51c7\u20262a7f) on Ethereum: ingress \u2192 normalize (Moralis) \u2192 decode \u2192 parse user-ops / Safe / proxy \u2192 build graph \u2192 persist. A closed-source MM router speaking several downstream dialects \u2014 Uniswap V2/V3 buy+sell, Curve tricrypto, Balancer, a V3 LP mint, a batch transfer \u2014 each pinned by its own selector; the decoder anchors the side / venue / pool / token / amounts per dialect and emits a typed buy/sell (or mint/transfer) headline, delegating the settled movements to the Transfer flow. Ships a 'latest N' sample and a hardcoded buy+sell set.",
      dynamic_step_budget: 8,
      feature_flags: {
        enable_grouping: true,
        enable_metadata_registry: true,
        enable_protocol_detection: true,
        enable_protocol_normalize: true,
        tx_graph: true
      },
      id: "wintermute",
      name: "Wintermute market maker",
      nodes: [
        {
          needs: [],
          node_name: null,
          options: {},
          step_name: "ingress",
          step_version: "0.1.0"
        },
        {
          needs: [
            "ingress"
          ],
          node_name: null,
          options: {},
          step_name: "normalize_moralis",
          step_version: "0.1.0"
        },
        {
          needs: [
            "normalize_moralis"
          ],
          node_name: null,
          options: {
            build_semantic: true,
            decode_events: true,
            decode_input: true,
            decode_internal_txs: true,
            decode_output: true,
            fetch_abi_from_s3: true,
            follow_proxy: true,
            guess_abi: true,
            use_default_abis: true
          },
          step_name: "decode",
          step_version: "0.2.0"
        },
        {
          needs: [
            "decode"
          ],
          node_name: null,
          options: {},
          step_name: "protocol_detect",
          step_version: "0.1.0"
        },
        {
          needs: [
            "protocol_detect"
          ],
          node_name: null,
          options: {},
          step_name: "protocol_normalize",
          step_version: "0.1.0"
        },
        {
          needs: [
            "protocol_normalize"
          ],
          node_name: null,
          options: {},
          step_name: "metadata_extract",
          step_version: "0.1.0"
        },
        {
          needs: [
            "decode"
          ],
          node_name: null,
          options: {},
          step_name: "parse_user_ops",
          step_version: "0.1.0"
        },
        {
          needs: [
            "parse_user_ops"
          ],
          node_name: null,
          options: {},
          step_name: "parse_safe_execution",
          step_version: "0.1.0"
        },
        {
          needs: [
            "parse_safe_execution"
          ],
          node_name: null,
          options: {},
          step_name: "parse_proxy",
          step_version: "0.1.0"
        },
        {
          needs: [
            "decode"
          ],
          node_name: null,
          options: {},
          step_name: "tx_graph_build",
          step_version: "0.1.0"
        },
        {
          needs: [
            "parse_proxy",
            "tx_graph_build"
          ],
          node_name: null,
          options: {},
          step_name: "persist",
          step_version: "2.0.0"
        },
        {
          needs: [
            "parse_proxy"
          ],
          node_name: null,
          options: {},
          step_name: "group",
          step_version: "0.1.0"
        },
        {
          needs: [
            "group"
          ],
          node_name: null,
          options: {},
          step_name: "context_signal_project",
          step_version: "0.1.0"
        },
        {
          needs: [
            "context_signal_project",
            "protocol_detect"
          ],
          node_name: null,
          options: {},
          step_name: "trigger_evaluate",
          step_version: "0.1.0"
        }
      ],
      samples: [
        {
          address: "0x51c72848c68a965f66fa7a88855f9f7784502a7f",
          chain_id: "0x1",
          count: 5,
          description: "The latest 5 transactions hitting the Wintermute market-maker router (0x51c7\u20262a7f) on Ethereum \u2014 discovered live via the explorer provider (no selector filter, so buys, sells and the other dialects are pulled). Each is fetched verbose and run through the MEV decode.",
          id: "wintermute-latest",
          name: "Wintermute latest (last 5 txs)",
          selector: null,
          source_kind: "latest",
          transaction_types: null,
          tx_hashes: [],
          tx_specs: []
        },
        {
          address: null,
          chain_id: "0x1",
          count: 2,
          description: "Two verified Wintermute txs on Ethereum: a Uniswap V3 pool sell (0x771d503f \u2014 anchors pool / amountSpecified / zeroForOne) and a Uniswap V3 pool buy (0x122067ed \u2014 anchors pool / amount0Delta / amount1Delta). The reproducible, no-network set.",
          id: "wintermute-buy-sell",
          name: "Wintermute buy + sell (Ethereum)",
          selector: null,
          source_kind: "tx_hashes",
          transaction_types: null,
          tx_hashes: [
            "0xd1d471339cfada7b11468c91c7350fb24c679e17e514d03bcedcbec088efc76e",
            "0x0c61fc3e8a5ca0dfae83aaf646eb1a91d17cfc9eb3e59dd9b0bb1b88066fe5d1"
          ],
          tx_specs: []
        }
      ],
      target_tier: "confirmed"
    },
    {
      category: "EVM",
      config: {
        batch: {
          prefetch: {
            max_prefetch_addresses: 1e3
          }
        }
      },
      description: "Chain-wide EVM pipeline: ingest every transaction in the block window, decode it against the whole bundled protocol catalogue (bridges, aggregators, exchanges, MEV bots, oracles, Safe) and record plain value transfers as native_transfer. Pair with discovery_mode=full_block on the system pipeline row \u2014 an event filter cannot see native transfers or selector-claimed protocols.",
      dynamic_step_budget: 8,
      feature_flags: {
        enable_grouping: true,
        enable_metadata_registry: true,
        enable_protocol_detection: true,
        enable_protocol_normalize: true,
        enable_tx_semantic: true,
        tx_graph: true
      },
      id: "evm-all-protocols",
      name: "EVM all protocols + native transfers",
      nodes: [
        {
          needs: [],
          node_name: null,
          options: {},
          step_name: "ingress",
          step_version: "0.1.0"
        },
        {
          needs: [
            "ingress"
          ],
          node_name: null,
          options: {},
          step_name: "normalize_quicknode",
          step_version: "0.1.0"
        },
        {
          needs: [
            "normalize_quicknode"
          ],
          node_name: null,
          options: {
            build_semantic: true,
            decode_events: true,
            decode_input: true,
            decode_internal_txs: true,
            decode_output: true,
            fetch_abi_from_s3: true,
            follow_proxy: true,
            guess_abi: true,
            use_default_abis: true
          },
          step_name: "decode",
          step_version: "0.2.0"
        },
        {
          needs: [
            "decode"
          ],
          node_name: null,
          options: {},
          step_name: "protocol_detect",
          step_version: "0.1.0"
        },
        {
          needs: [
            "protocol_detect"
          ],
          node_name: null,
          options: {},
          step_name: "protocol_normalize",
          step_version: "0.1.0"
        },
        {
          needs: [
            "protocol_normalize"
          ],
          node_name: null,
          options: {},
          step_name: "metadata_extract",
          step_version: "0.1.0"
        },
        {
          needs: [
            "decode"
          ],
          node_name: null,
          options: {},
          step_name: "parse_user_ops",
          step_version: "0.1.0"
        },
        {
          needs: [
            "parse_user_ops"
          ],
          node_name: null,
          options: {},
          step_name: "parse_safe_execution",
          step_version: "0.1.0"
        },
        {
          needs: [
            "parse_safe_execution"
          ],
          node_name: null,
          options: {},
          step_name: "parse_proxy",
          step_version: "0.1.0"
        },
        {
          needs: [
            "parse_proxy"
          ],
          node_name: null,
          options: {},
          step_name: "context_proxy_write",
          step_version: "0.1.0"
        },
        {
          needs: [
            "decode"
          ],
          node_name: null,
          options: {},
          step_name: "tx_graph_build",
          step_version: "0.1.0"
        },
        {
          needs: [
            "parse_proxy"
          ],
          node_name: null,
          options: {},
          step_name: "reorg_detector",
          step_version: "0.1.0"
        },
        {
          needs: [
            "reorg_detector"
          ],
          node_name: null,
          options: {},
          step_name: "finality_promote",
          step_version: "0.1.0"
        },
        {
          needs: [
            "finality_promote",
            "tx_graph_build"
          ],
          node_name: null,
          options: {},
          step_name: "persist",
          step_version: "2.0.0"
        },
        {
          needs: [
            "persist"
          ],
          node_name: null,
          options: {},
          step_name: "index_project",
          step_version: "0.1.0"
        },
        {
          needs: [
            "decode"
          ],
          node_name: null,
          options: {},
          step_name: "tx_action",
          step_version: "0.1.0"
        },
        {
          needs: [
            "parse_proxy"
          ],
          node_name: null,
          options: {},
          step_name: "group",
          step_version: "0.1.0"
        },
        {
          needs: [
            "group"
          ],
          node_name: null,
          options: {},
          step_name: "context_signal_project",
          step_version: "0.1.0"
        },
        {
          needs: [
            "context_signal_project",
            "protocol_detect"
          ],
          node_name: null,
          options: {},
          step_name: "trigger_evaluate",
          step_version: "0.1.0"
        }
      ],
      samples: [],
      target_tier: "confirmed"
    }
  ]
};

// src/pipeline/templates.ts
function findTemplate(id) {
  if (!id) return void 0;
  return DEFAULT_TEMPLATES.find((t) => t.id === id);
}
function templateSamplesFor(templateId) {
  return findTemplate(templateId)?.samples ?? [];
}
function stackedPosition(index) {
  return { x: 120, y: 80 + index * 120 };
}
function instantiateTemplate(template, overrides = {}) {
  const doc = createPipelineDoc({
    name: overrides.name ?? template.name,
    description: overrides.description ?? template.description,
    targetTier: overrides.targetTier ?? template.targetTier ?? LatencyTier.CONFIRMED,
    dynamicStepBudget: overrides.dynamicStepBudget ?? template.dynamicStepBudget ?? 8,
    featureFlags: overrides.featureFlags ?? template.featureFlags,
    // Deep-cloned: the gallery is a module-level singleton, so aliasing it here
    // would let an edit to one instantiated doc rewrite the template itself.
    configOverlay: overrides.configOverlay ?? (template.config ? structuredClone(template.config) : void 0),
    // Stamp the originating template so the run console can offer this
    // template's run samples (carried as `metadata.template_id` on the wire).
    templateId: overrides.templateId ?? template.id,
    ...overrides,
    nodes: []
  });
  const nodes = [];
  const renamed = /* @__PURE__ */ new Map();
  template.nodes.forEach((tn, i) => {
    const declaredName = tn.nodeName ?? tn.stepName;
    const nodeName = uniqueNodeName({ ...doc, nodes }, declaredName);
    if (!renamed.has(declaredName)) renamed.set(declaredName, nodeName);
    nodes.push({
      id: newNodeId(),
      nodeName,
      stepName: tn.stepName,
      stepVersion: tn.stepVersion ?? "0.1.0",
      needs: tn.needs ?? [],
      options: tn.options ?? {},
      position: stackedPosition(i)
    });
  });
  for (const node of nodes) {
    node.needs = node.needs.map((dep) => renamed.get(dep) ?? dep);
  }
  doc.nodes = nodes;
  return doc;
}
var DEFAULT_TEMPLATES = templatesFromWire(
  template_catalog_generated_default
);
function bumpVersion(version, bump) {
  const parts = version.split(".").map((p) => Number.parseInt(p, 10));
  let [major = 0, minor = 0, patch = 0] = parts.map((n) => Number.isFinite(n) ? n : 0);
  if (bump === "major") {
    major += 1;
    minor = 0;
    patch = 0;
  } else if (bump === "minor") {
    minor += 1;
    patch = 0;
  } else {
    patch += 1;
  }
  return `${major}.${minor}.${patch}`;
}

// src/pipeline/persistence.ts
function snapshotVersion(doc, bump, opts = {}) {
  const version = bumpVersion(doc.version, bump);
  const createdAt = opts.now ?? (/* @__PURE__ */ new Date()).toISOString();
  const nextDoc = { ...doc, version, updatedAt: createdAt };
  return {
    docId: doc.id,
    version,
    label: opts.label,
    createdAt,
    author: opts.author,
    note: opts.note,
    doc: nextDoc
  };
}

// src/pipeline/simulation.ts
var SimulationEventKind = {
  /** Replay a historical transaction through the pipeline. */
  REPLAY_TX: "replay_tx",
  /** Inject a mock reorg over a block range. */
  REORG: "reorg",
  /** Promote a tx to a higher finality tier. */
  FINALITY: "finality"
};
function createReplayTxEvent(init) {
  return { id: newNodeId("ev"), kind: SimulationEventKind.REPLAY_TX, ...init };
}
function createReorgEvent(init) {
  return { id: newNodeId("ev"), kind: SimulationEventKind.REORG, ...init };
}
function createScenario(init = {}) {
  return {
    id: init.id ?? newNodeId("sim"),
    name: init.name ?? "New scenario",
    targetTier: init.targetTier ?? "confirmed",
    replayMode: init.replayMode ?? null,
    events: init.events ?? []
  };
}

// src/pipeline/integrations.ts
function isRunnableIntegration(row) {
  return row.enabled !== false && Boolean(row.live_version_id) && Boolean(row.live_function_version);
}
function unselectableReason(row, access) {
  if (row.enabled === false) return "Disabled";
  if (!row.live_version_id) return "No version published yet";
  if (!row.live_function_version) return "Promoted before versions were pinned \u2014 promote it again";
  if (access?.blind) return "No access granted \u2014 it would see nothing";
  return null;
}
function connectorsFromIntegrations(rows) {
  return rows.filter((row) => row.kind !== "internal" && isRunnableIntegration(row)).map((row) => ({
    id: row.runtime_id,
    name: row.name || row.runtime_id,
    description: row.description ?? void 0
  }));
}
function integrationsFromResponse(body) {
  if (Array.isArray(body)) return body;
  if (typeof body !== "object" || body === null) return [];
  const items = body.items;
  return Array.isArray(items) ? items : [];
}
function integrationStarterFor(runtimeId) {
  if (runtimeId.startsWith("python")) return PY_INTEGRATION_STARTER;
  if (runtimeId.startsWith("node") || runtimeId.startsWith("typescript")) return NODE_INTEGRATION_STARTER;
  return null;
}
var PY_INTEGRATION_STARTER = `def handler(event, context):
    """Called once per transaction by a pipeline's custom_step node.

    event["canonical"]   the transaction (chain_id, tx_hash, from/to, value, ...)
    event["enrichments"] what earlier steps produced
    event["options"]     this node's code_options

    Return enrichments to append. Returning nothing appends nothing.
    """
    tx = event.get("canonical") or {}
    return {
        "enrichments": [
            {
                "kind": "custom",
                "payload": {
                    "tx_hash": tx.get("tx_hash"),
                    "chain_id": tx.get("chain_id"),
                    "note": "hello from my integration",
                },
            }
        ]
    }
`;
var NODE_INTEGRATION_STARTER = `export async function handler(event) {
  // Called once per transaction by a pipeline's custom_step node.
  //   event.canonical    the transaction (chain_id, tx_hash, from/to, value, ...)
  //   event.enrichments  what earlier steps produced
  //   event.options      this node's code_options
  // Return enrichments to append. Returning nothing appends nothing.
  const tx = event.canonical ?? {};
  return {
    enrichments: [
      { kind: "custom", payload: { tx_hash: tx.tx_hash, chain_id: tx.chain_id, note: "hello from my integration" } },
    ],
  };
}
`;

// src/pipeline/grants.ts
var ADDRESS_KIND = {
  kind: "address",
  label: "Address",
  keyLabel: "Address",
  hint: "Transactions where this address is the sender, the recipient or a participant.",
  placeholder: "0x\u2026 or a Solana / Bitcoin address",
  keyControl: "text",
  chainScoped: true
};
var GRANT_SUBJECT_KINDS = [
  {
    kind: "chain",
    label: "Whole chain",
    keyLabel: "Chain",
    hint: "Every transaction the pipeline processes on this chain. The broadest grant \u2014 use it while developing, then narrow.",
    placeholder: "",
    keyControl: "chain",
    chainScoped: false
  },
  ADDRESS_KIND,
  {
    kind: "label",
    label: "Label",
    keyLabel: "Label id",
    hint: "Transactions the pipeline labelled with this label id. Only matches if the labelling step runs BEFORE the integration.",
    placeholder: "label id",
    keyControl: "text",
    chainScoped: true
  },
  {
    kind: "tag",
    label: "Tag",
    keyLabel: "Tag id",
    hint: "Transactions carrying this tag id. Only matches if tagging runs BEFORE the integration.",
    placeholder: "tag id",
    keyControl: "text",
    chainScoped: true
  },
  {
    kind: "protocol",
    label: "Protocol",
    keyLabel: "Protocol slug",
    hint: "Transactions attributed to this protocol. Only matches if attribution runs BEFORE the integration.",
    placeholder: "protocol slug",
    keyControl: "text",
    chainScoped: true
  },
  {
    kind: "entity",
    label: "Entity",
    keyLabel: "Entity id",
    hint: "Transactions attributed to this entity id. Only matches if attribution runs BEFORE the integration.",
    placeholder: "entity id",
    keyControl: "text",
    chainScoped: true
  }
];
function grantSubjectKindInfo(kind) {
  return GRANT_SUBJECT_KINDS.find((row) => row.kind === kind) ?? ADDRESS_KIND;
}
function normalizeSubjectKey(kind, subjectKey, chainId) {
  const value = (subjectKey || "").trim();
  if (!value) return value;
  if (kind === "address") return normalizeAddress(value, chainId ?? void 0);
  if (kind === "chain") return toCanonicalChainId(value) ?? value;
  return value;
}
function validateGrantDraft(draft) {
  const chainId = draft.chain_id?.trim() || null;
  const key = normalizeSubjectKey(draft.subject_kind, draft.subject_key, chainId);
  if (!key) return "A subject is required \u2014 a grant that names nothing grants nothing.";
  if (draft.subject_kind === "chain" && !resolveChain(key)) {
    return "That is not a chain the platform knows, so no transaction would ever match it.";
  }
  if (draft.subject_kind === "address" && !isValidAddress(key, chainId)) {
    return "That does not look like an address. Check for a truncated paste.";
  }
  if (chainId && !resolveChain(chainId)) {
    return "The narrowing chain is not one the platform knows.";
  }
  if (draft.expires_at_ms != null && draft.expires_at_ms <= Date.now()) {
    return "An expiry in the past grants nothing.";
  }
  return null;
}
function isGrantActive(grant, nowMs = Date.now()) {
  if (grant.enabled === false) return false;
  if (grant.expires_at_ms != null && nowMs >= grant.expires_at_ms) return false;
  return true;
}
function describeGrant(grant) {
  const info = grantSubjectKindInfo(grant.subject_kind);
  const subject = grant.subject_kind === "chain" ? chainDisplayName(grant.subject_key) : grant.subject_key;
  const scope = grant.chain_id ? ` on ${chainDisplayName(grant.chain_id)}` : "";
  return `${info.label}: ${subject}${scope}`;
}
function emptyGrantDraft() {
  return { subject_kind: "chain", subject_key: DEFAULT_CHAIN_ID };
}
function inactiveGrantReason(grant, nowMs = Date.now()) {
  if (grant.enabled === false) return "Disabled";
  if (grant.expires_at_ms != null && nowMs >= grant.expires_at_ms) return "Expired";
  return null;
}
function accessSummary(grants, nowMs = Date.now()) {
  const total = grants.length;
  const active = grants.filter((grant) => isGrantActive(grant, nowMs)).length;
  const blind = active === 0;
  let sentence;
  if (total === 0) {
    sentence = "This integration has no access grants, so it will be skipped by every pipeline. Grant it a subject to make it run.";
  } else if (blind) {
    sentence = `All ${total} grant${total === 1 ? "" : "s"} ${total === 1 ? "is" : "are"} disabled or expired, so this integration will be skipped by every pipeline.`;
  } else if (grants.some((grant) => isGrantActive(grant, nowMs) && grant.subject_kind === "chain")) {
    sentence = `Sees every transaction on ${active === 1 ? "the granted chain" : "the granted subjects"}.`;
  } else {
    sentence = `Sees ${active} granted subject${active === 1 ? "" : "s"}.`;
  }
  return { total, active, blind, sentence };
}
function grantsFromResponse(body) {
  if (Array.isArray(body)) return body;
  if (typeof body !== "object" || body === null) return [];
  const items = body.items;
  return Array.isArray(items) ? items : [];
}

export { BODY_SCOPES, CONFIG_LAYER_LABEL, CONFIG_LAYER_ORDER, Completeness, ConfigLayerKind, DEFAULT_BODY_SCOPE, DEFAULT_RETRY_POLICY, DEFAULT_STEP_CATALOG, DEFAULT_TEMPLATES, FAILURE_POLICIES, FAILURE_POLICY_DESCRIPTION, FAILURE_POLICY_LABEL, FailurePolicy, GRANT_SUBJECT_KINDS, LATENCY_TIERS, LatencyTier, STEP_CATEGORY_LABEL, STEP_INJECTION_SCOPE_LABEL, STEP_INJECTION_SCOPE_ORDER, STEP_STATUS_LABEL, SimulationEventKind, StepCategory, StepDecisionReason, StepInjectionScope, StepStatus, TIER_LABEL, accessSummary, applyStepInjections, bumpVersion, catalogFromWire, connectorsFromIntegrations, createPipelineDoc, createReorgEvent, createReplayTxEvent, createScenario, deepMergeValues, defaultStepMetadata, describeGrant, docFromWire, docToWire, effectiveInjections, emptyGrantDraft, emptyHierarchicalConfig, emptyLayer, findNode, findStep, findTemplate, grantSubjectKindInfo, grantsFromResponse, groupByCategory, inactiveGrantReason, indexCatalog, injectionFromWire, injectionShadowedBy, injectionToWire, instantiateTemplate, integrationStarterFor, integrationsFromResponse, isAtLeast, isDeployablePipeline, isEnrichmentStep, isGrantActive, isRunnableIntegration, isSkipReason, isSkipped, issueFromWire, issuesFromWire, newNodeId, normalizeSubjectKey, outcomeReason, renderPipelineMermaid, resolveConfig, retriesEnabled, runResultFromWire, shadowedInjections, snapshotVersion, stableHash, stepMetadataFromWire, stepRefId, summarizeOutcomes, templateFromWire, templateSamplesFor, templatesFromWire, topoSort, uniqueNodeName, unselectableReason, validateGrantDraft, validatePipelineDoc };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map